/**
 * The runtime (web-plan.md F1) — the web twin of `app/src/runtime/`: owns the
 * immutable vanilla store, the socket, and every async flow. Components send
 * commands (plain method calls); renderer adapters subscribe to snapshots.
 *
 * The one discipline ported wholesale from the desktop: every network
 * handler snapshots what it needs, awaits, then checks the account lifetime
 * and **re-checks `feed.channelId` before writing**. `appendMessages` dedupes on `seq`, and
 * `seq` is per channel — fifty of channel A's messages would splice silently
 * into B's history without the guard.
 */
import { freeze, produce } from "immer";
import { createStore, type StoreApi } from "zustand/vanilla";

import type { Message, SendBody, ServerEvent } from "../gen/dto";

import * as api from "../lib/api/rest";
import { UploadError, upload } from "../lib/api/uploads";
import { ChatSocket, type SocketEvent } from "../lib/api/ws";
import { type Dict, type Lang, detectLang, dict } from "../lib/i18n";
import {
  assertCurrent,
  currentLifetime,
  endLifetime,
  isCurrent,
  sessionDelay,
  type SessionLifetime,
} from "../lib/lifetime";
import { ORIGINAL_MAX_BYTES, photoWeights, photoWeightsSupported } from "../lib/photo";
import { maskedPreview } from "../lib/preview";
import { armReauthWindow, inReauthWindow, session, setSession } from "../lib/session";
import * as sounds from "../lib/sounds";
import { isValidTtl } from "../lib/ttl";
import {
  VoiceSession,
  type VoiceEvents,
  shareViewSupported,
  voiceSupported,
} from "../lib/voice/session";
import { parseMarkdown, splitMessage } from "../lib/wasm";
import * as mut from "./mutators";
import {
  AFK_AFTER_MS,
  PAGE_SIZE,
  PENDING_SEQ,
  RING_FOR_MS,
  TICK_MS,
  WINDOW_SIZE,
  type ChannelCardEntry,
  type LinkPreviewEntry,
  type UiState,
  channelCardKey,
  initialState,
  isDmRoom,
  isPet,
} from "./types";

/** The message that takes a Stop back (pets-plan.md D368) —
 * `echo_wire::command::RESTART` behind its sigil. */
const RESTART_TEXT = "/restart";

const MAX_SPLIT_PARTS = 10;

/** Page previews in the air at once, across the app (D280): a feed full of
 * links is otherwise a burst of fetches, each of which may hold the gateway
 * for nine seconds and all of which draw on one per-account budget. */
const PREVIEW_LANE_WIDTH = 4;
/** Channel cards in the air at once (D281) — cheap to answer, but budgeted at
 * sixty a minute per account, and a slow page must not queue them. */
const CARD_LANE_WIDTH = 4;
/** How long a link card that failed for want of time — the budget, a fetch
 * that outlived its request, the network — waits before one may ask again. */
const LINK_RETRY_MS = 60_000;

/** Captured at Send, never reconstructed from whichever screen opens later. */
interface SendIntent {
  readonly lifetime: SessionLifetime;
  readonly accountId: number;
  readonly selfName: string;
  readonly channel: number;
  readonly replyTo: number | null;
  readonly editing: number | null;
  readonly parts: ReadonlyArray<{
    readonly text: string;
    readonly entities: import("../gen/dto").Entity[];
    readonly nonce: string;
  }>;
  readonly staged: ReadonlyArray<Readonly<StagedFile>>;
  readonly unlimited: boolean;
  readonly pause: number;
  /** The sender closed the link's card in the composer (D289): every part of
   * the send says so, and so does each optimistic bubble, so no card flashes
   * up under one before its echo arrives. */
  readonly noLinkPreview: boolean;
  /** What the composer's clock stood at for this channel when the send was
   * made (disappearing-messages.md D298), seconds, or null for a message that
   * stays. Read by the runtime and not handed over by the box, so that a voice
   * note and a dropped file go out under it too; every part of a split send
   * carries it. Always null for an edit: `PATCH` neither sets nor moves a
   * deadline (D296). */
  readonly ttlS: number | null;
}

type SendContext = Omit<SendIntent, "parts" | "staged" | "noLinkPreview"> & {
  readonly nonce: string;
  readonly maxChars: number;
};

/** What a send says beside its words and its files. */
export interface SendOptions {
  /** `no_link_preview` (link-previews.md D289): the composer's bar was
   * closed on the draft's link. */
  readonly noLinkPreview?: boolean;
}

/** Finishing a recording may await an encoder; its target is already fixed. */
export type PreparedSend = (text: string, staged?: StagedFile[], options?: SendOptions) => void;

export type RuntimeTransitionKind = "screen" | "center" | "sheet" | "pop" | "tab" | "toast";

/** Rendering and commit timing belong to the host, never the network runtime. */
export interface RuntimeHost {
  transition(kind: RuntimeTransitionKind, commit: () => void): void;
}

export class Runtime {
  readonly store: StoreApi<UiState>;
  get state(): UiState {
    return this.store.getState();
  }
  private started = false;
  private disposed = false;
  private resumedLifetime: SessionLifetime | null = null;
  private unregisterUnauthorized: (() => void) | null = null;
  private channelGeneration = 0;
  private requestedChannel: number | null = null;
  private cardGeneration = 0;
  private readonly activityListener = () => this.noteActivity();
  private readonly visibilityListener = () => {
    if (document.visibilityState !== "visible") return;
    this.noteActivity();
    // A hidden tab's timers run late, by as much as a minute: what came due
    // behind it goes before anybody looks at it again (D298).
    this.expireDue();
  };
  private socket: ChatSocket | null = null;
  readonly lang: Lang;
  readonly t: Dict;
  /** One worker drains this — sends must stay in composed order, because
   * reconcile adopts by nonce and patches in place. */
  private sendQueue: SendIntent[] = [];
  private sending = false;
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  /** Armed for the nearest deadline among the messages this client holds a
   * copy of (D298); null while none of them is going anywhere. */
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private toastTimers = new Set<ReturnType<typeof setTimeout>>();
  /** Link cards (D280, D281): the two lines their requests wait in, and what
   * is queued or in the air in each — the dedupe, kept apart from the state
   * so that a `loading` a torn-down runtime left behind is asked again. */
  private readonly previewLane = new Lane(PREVIEW_LANE_WIDTH);
  private readonly cardLane = new Lane(CARD_LANE_WIDTH);
  private readonly previewsAsked = new Set<string>();
  private readonly cardsAsked = new Set<string>();
  /** Channels an invite link is joining right now: a second press waits. */
  private readonly linkJoins = new Set<number>();
  /** Per chat, the number of the `/` list last asked for (pets-plan.md D360):
   * an answer to an older ask — or to one a pet's move dropped since — is
   * thrown away rather than written over the newer truth. */
  private readonly commandAsks = new Map<number, number>();
  private commandAskSeq = 0;
  private lastActivityMs = Date.now();
  /** Callbacks for the host layer (sounds, notifications) — F3 wires them. */
  onRing: ((start: boolean) => void) | null = null;

  constructor(
    private readonly host: RuntimeHost = {
      transition: (_kind, commit) => commit(),
    },
  ) {
    this.store = createStore<UiState>(() => freeze(initialState(), true));
    this.lang = detectLang();
    this.t = dict(this.lang);
    // The server words its refusals in the window's language (D339).
    api.setRequestLang(this.lang);
  }

  /** Bootstrap owns this registration; rerendering and route mounts are harmless. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.disposed = false;
    try {
      // A word this build does not know leaves the default: a tab saved by a
      // newer build is not one this one can draw. `pets` is kept even before
      // `/v1/me` has said whether the server knows pets — the sidebar draws
      // All in its place until it does, and for good on a server that does
      // not (pets-plan.md §10).
      const tab = localStorage.getItem("echofloat.tab");
      if (tab === "all" || tab === "channels" || tab === "friends" || tab === "pets") {
        this.mutate((s) => {
          s.tab = tab;
        });
      }
    } catch {
      /* Storage can be unavailable; navigation still works in memory. */
    }
    this.unregisterUnauthorized =
      api.setUnauthorizedHandler(() => {
        if (!inReauthWindow()) void this.logout();
      }) ?? null;
    window.addEventListener("pointerdown", this.activityListener, {
      passive: true,
    });
    window.addEventListener("keydown", this.activityListener, { passive: true });
    document.addEventListener("visibilitychange", this.visibilityListener);
  }

  /** Technical teardown keeps the stored login. A subsequent resume reconnects. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    endLifetime();
    this.releaseResources();
    this.unregisterUnauthorized?.();
    this.unregisterUnauthorized = null;
    if (this.started) {
      window.removeEventListener("pointerdown", this.activityListener);
      window.removeEventListener("keydown", this.activityListener);
      document.removeEventListener("visibilitychange", this.visibilityListener);
      this.started = false;
    }
    this.mutate((s) => {
      s.connected = false;
      s.busy = false;
      s.voice = null;
      s.ringing = null;
      s.toasts = [];
    });
  }

  private transition(kind: RuntimeTransitionKind, commit: () => void): void {
    const lifetime = currentLifetime();
    this.host.transition(kind, () => {
      if (isCurrent(lifetime)) commit();
    });
  }

  /** UI commands report errors only to their originating account. Multi-step
   * commands use check() before issuing another request; apply never runs late. */
  private async sessionAction<T>(
    request: (check: () => void) => Promise<T>,
    apply?: (result: T) => void,
  ): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const result = await request(() => assertCurrent(lifetime));
      if (isCurrent(lifetime)) apply?.(result);
    } catch (error) {
      if (isCurrent(lifetime)) throw error;
    }
  }

  private mutate(fn: (draft: UiState) => void): void {
    const before = this.state;
    let next = produce(before, fn);
    // Disappearing messages (D298): whatever a change brought in — a history
    // page, which the gateway does not filter (D297); a row's line out of
    // GET /v1/state; an edit's frame that raced the deadline — nothing already
    // past its deadline is ever published, and the timer stands for the
    // nearest deadline of what is. Structural sharing says when to look: the
    // copies that carry a deadline, and the clock they are read against, can
    // only have changed if one of these three did.
    const timed =
      next.feed.bubbles !== before.feed.bubbles ||
      next.rooms !== before.rooms ||
      next.clock !== before.clock;
    if (timed) next = this.withoutExpired(next);
    this.store.setState(next, true);
    if (timed) this.armExpiry();
  }

  private newSocket(): ChatSocket {
    const lifetime = currentLifetime();
    const socket = new ChatSocket((event) => {
      if (isCurrent(lifetime) && this.socket === socket) this.onSocketEvent(event);
    });
    return socket;
  }

  private releaseResources(): void {
    this.channelGeneration += 1;
    this.cardGeneration += 1;
    this.requestedChannel = null;
    this.resumedLifetime = null;
    this.socket?.dispose();
    this.socket = null;
    this.stopTick();
    this.stopExpiry();
    for (const timer of this.toastTimers) clearTimeout(timer);
    this.toastTimers.clear();
    this.previewLane.clear();
    this.cardLane.clear();
    this.previewsAsked.clear();
    this.cardsAsked.clear();
    this.linkJoins.clear();
    this.commandAsks.clear();
    this.sendQueue = [];
    this.sending = false;
    this.voice?.leave();
    this.voice = null;
    this.sharePainterOwner = null;
    this.onRing?.(false);
  }

  /** Logout/account replacement invalidates I/O before any synchronous close callback. */
  private resetSession(): void {
    endLifetime();
    this.releaseResources();
    setSession(null);
    this.store.setState(freeze(initialState(), true), true);
  }

  // -------------------------------------------------------------------------
  // Login / logout

  async login(username: string, password: string): Promise<void> {
    this.start();
    this.resetSession();
    await this.authenticate(username, password);
  }

  private async authenticate(username: string, password: string): Promise<void> {
    const lifetime = currentLifetime();
    if ("Notification" in window && Notification.permission === "default") {
      void Notification.requestPermission();
    }
    this.mutate((s) => {
      s.busy = true;
      s.notice = null;
    });
    try {
      const { jwt } = await api.login(username, password);
      if (!isCurrent(lifetime)) return;
      setSession({ jwt, userId: 0, base: "" });
      this.mutate((s) => {
        s.selfUsername = username;
      });
      // Best-effort identity: a gateway too old to know /v1/me still logs in.
      try {
        const me = await api.fetchMe();
        if (!isCurrent(lifetime)) return;
        setSession({ jwt, userId: me.user_id, base: "" });
        this.adoptMe(me, true);
        // D311: the gateway refuses the socket too; the window shows the
        // address card, and `confirmAddress` opens the socket afterwards.
        if (me.must_confirm_email === true) return;
      } catch {
        if (!isCurrent(lifetime)) return;
        // The directory's username match becomes the fallback identity.
      }
      this.startSocket();
    } catch (error) {
      if (isCurrent(lifetime)) throw error;
    } finally {
      if (isCurrent(lifetime)) {
        this.mutate((s) => {
          s.busy = false;
        });
      }
    }
  }

  async register(username: string, email: string, password: string): Promise<void> {
    this.start();
    this.resetSession();
    const lifetime = currentLifetime();
    this.mutate((s) => {
      s.busy = true;
      s.notice = null;
    });
    try {
      await api.register(username, email, password);
      if (!isCurrent(lifetime)) return;
      await this.authenticate(username, password);
      if (!isCurrent(lifetime)) return;
    } catch (error) {
      if (isCurrent(lifetime)) throw error;
    } finally {
      if (isCurrent(lifetime)) {
        this.mutate((s) => {
          s.busy = false;
        });
      }
    }
  }

  // -------------------------------------------------------------------------
  // Confirming an address (gateway/src/confirm.rs)
  //
  // Each step is its own method rather than one state machine here, because
  // the state between them — which screen, which ticket, what was typed — is
  // the login card's and nothing else in the app has a use for it. What the
  // runtime owns is the two things a view must not: the `busy` flag every
  // control reads, and the lifetime check that keeps a reply from a signed-out
  // session out of the store.

  private async step<T>(work: () => Promise<T>): Promise<T> {
    const lifetime = currentLifetime();
    this.mutate((s) => {
      s.busy = true;
      s.notice = null;
    });
    try {
      return await work();
    } finally {
      if (isCurrent(lifetime)) {
        this.mutate((s) => {
          s.busy = false;
        });
      }
    }
  }

  /** Submits the sign-up form. Nothing exists afterwards but a ticket and a
   * letter — see {@link confirmRegistration}. */
  registerStart(username: string, email: string, password: string): Promise<api.CodeSent> {
    this.start();
    return this.step(() => api.registerStart(username, email, password, this.lang));
  }

  /** The account appears here, and the sign-in that follows is the same one
   * one-step registration always did. */
  async confirmRegistration(
    ticket: string,
    code: string,
    username: string,
    password: string,
  ): Promise<void> {
    this.start();
    await this.step(() => api.registerConfirm(ticket, code));
    this.resetSession();
    await this.authenticate(username, password);
  }

  /** «Send it again» — the same code, for as long as it lives. */
  resendCode(ticket: string): Promise<api.CodeSent> {
    return this.step(() => api.resendCode(ticket, this.lang));
  }

  /** `login` is an address or a username. Answers the same whether or not it
   * names an account here, which is why nothing branches on the result. */
  passwordForgot(login: string): Promise<api.CodeSent> {
    this.start();
    return this.step(() => api.passwordForgot(login, this.lang));
  }

  passwordVerify(ticket: string, code: string): Promise<{ reset_token: string }> {
    return this.step(() => api.passwordVerify(ticket, code));
  }

  /** Sets the password and ends every session the account had — including,
   * if the person was somehow signed in here, this one. The card comes back
   * to sign-in either way. */
  async passwordReset(resetToken: string, password: string): Promise<void> {
    await this.step(() => api.passwordReset(resetToken, password));
  }

  /** Resume from a stored session (page load). Identity is re-adopted the
   * same best-effort way login does it — a reloaded page otherwise holds an
   * empty username, and the first profile save would send it. */
  resume(): boolean {
    this.start();
    const s = session();
    if (!s) return false;
    const lifetime = currentLifetime();
    if (this.resumedLifetime === lifetime) return true;
    this.resumedLifetime = lifetime;
    void api
      .fetchMe()
      .then((me) => {
        if (!isCurrent(lifetime)) return;
        this.adoptMe(me, true);
        // D311: the gateway refuses this account's socket until it proves an
        // address, and a socket that is refused retries forever. The window
        // shows the address card; `confirmAddress` opens a fresh one.
        if (me.must_confirm_email === true) {
          this.socket?.dispose();
          this.socket = null;
        }
      })
      .catch(() => {});
    // Not held back for the answer above: a reload of a confirmed account —
    // every reload but the rare one — reconnects without the extra round trip.
    this.startSocket();
    return true;
  }

  async logout(): Promise<void> {
    this.resetSession();
  }

  // -------------------------------------------------------------------------
  // An account that has not proven its address (D311)

  /** Mails a code to the address the person typed — which may differ from
   * the one on record. */
  addressStart(email: string): Promise<api.CodeSent> {
    return this.step(() => api.addressStart(email, this.lang));
  }

  /** Writes the address with its proof, lifts the gate, and opens the app:
   * the same token works from here on, nothing is signed in again. */
  async confirmAddress(ticket: string, code: string): Promise<void> {
    const lifetime = currentLifetime();
    const me = await this.step(() => api.addressConfirm(ticket, code));
    if (!isCurrent(lifetime)) return;
    this.adoptMe(me);
    this.startSocket();
  }

  // -------------------------------------------------------------------------
  // Changing a proven address (D312)
  //
  // Which step is open, which ticket and which grant are the dialog's own
  // (`views/change-email.tsx`), for the reason the login card keeps its: nothing
  // else in the app has a use for them. Not through `step`: its `busy` is the
  // login card's, and a flag that greys the whole window has no business
  // here. What the runtime adds is the language the letters are written in,
  // and the toast at the end.

  /** The address on record as the gateway masks it, or `null` for none. */
  async emailOnRecord(): Promise<string | null> {
    return (await api.fetchEmailOnRecord()).email;
  }

  emailChangeStart(): Promise<api.CodeSent> {
    return api.emailChangeStart(this.lang);
  }

  /** Spends the old mailbox's code; the grant it buys is what every letter to
   * a new address is paid with. */
  async emailChangeVerify(ticket: string, code: string): Promise<string> {
    return (await api.emailChangeVerify(ticket, code)).change_token;
  }

  emailChangeNew(changeToken: string, email: string): Promise<api.CodeSent> {
    return api.emailChangeNew(changeToken, email, this.lang);
  }

  /** Writes the new address, then says so the way the app says things that
   * happened: a toast, which outlives the dialog closing under it. */
  async emailChangeConfirm(ticket: string, code: string): Promise<void> {
    const lifetime = currentLifetime();
    await api.emailChangeConfirm(ticket, code);
    if (isCurrent(lifetime)) this.pushToast(this.t.emailChanged(), null);
  }

  /** `probe` for the answer of `GET /v1/me` itself — the one that carries
   * `pets`, the capability probe (pets-plan.md §10). Every other route that
   * answers a UserJson (the profile, the avatar, the status) leaves the key
   * out, and must not be read as a server that has forgotten pets. */
  private adoptMe(me: import("../gen/dto").UserJson, probe = false): void {
    this.mutate((s) => {
      s.selfUserId = me.user_id;
      s.selfUsername = me.username;
      s.directory.selfId = me.user_id;
      s.account.displayName = me.display_name;
      s.account.bio = me.bio;
      s.account.avatarFileId = me.avatar_file_id;
      s.account.createdAt = me.created_at;
      s.account.bubbleLight = me.bubble_light;
      s.account.bubbleDark = me.bubble_dark;
      s.account.manualStatus = normalizeManual(me.manual_status);
      s.account.isAdmin = me.is_admin;
      if (probe) s.account.pets = me.pets ?? null;
      s.mustConfirmEmail = me.must_confirm_email === true;
      s.directory.people[me.user_id] = {
        username: me.username,
        displayName: me.display_name,
        bio: me.bio,
        avatarFileId: me.avatar_file_id,
        createdAt: me.created_at,
        bubbleLight: me.bubble_light,
        bubbleDark: me.bubble_dark,
      };
    });
  }

  private startSocket(): void {
    const s = session();
    if (!s) return;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    this.socket?.dispose();
    this.socket = this.newSocket();
    this.resumedLifetime = currentLifetime();
    this.socket.start(`${proto}://${location.host}/v1/chat/ws`, s.jwt);
    this.startTick();
    // A teardown stood the expiry timer down and kept the feed (`dispose`):
    // what it holds still goes at its deadline, connected or not (D298).
    this.armExpiry();
  }

  // -------------------------------------------------------------------------
  // Socket events

  private onSocketEvent(e: SocketEvent): void {
    switch (e.t) {
      case "status":
        this.mutate((s) => {
          s.connected = e.connected;
        });
        return;
      case "ready": {
        this.mutate((s) => {
          // A fresh connection is "here" as far as the server is concerned.
          s.presence.selfAfk = false;
          if (mut.selfIdOf(s.directory) === 0) {
            s.directory.selfId = e.user_id;
            s.selfUserId = e.user_id;
          }
        });
        void this.sync();
        return;
      }
      case "auth_rejected": {
        // The socket is handed its JWT once and cannot refresh one — except
        // right after a password change, where the fresh JWT exists and this
        // rejection is the revocation doing its job (R44).
        if (inReauthWindow()) {
          const s = session();
          if (s) {
            this.startSocket();
            return;
          }
        }
        void this.logout();
        this.mutate((s) => {
          s.notice = e.message;
        });
        return;
      }
      case "event":
        this.onServerEvent(e.event);
        return;
    }
  }

  private onServerEvent(e: ServerEvent): void {
    switch (e.t) {
      case "message":
        this.onMessage(e as unknown as Message & { t: string });
        return;
      case "message_edited":
        this.onEdited(e as unknown as Message & { t: string });
        return;
      case "message_deleted":
        this.mutate((s) => this.forget(s, e.channel_id, e.seq));
        return;
      case "message_expiring":
        // Somebody other than the author has read them, so their clocks have
        // started (D302). Nothing is removed here: `mutate` runs the expiry
        // pass over every snapshot it makes and re-arms the timer from it, so
        // writing the deadlines is the whole of this.
        this.mutate((s) => {
          mut.armExpiry(s.feed, s.rooms, e.channel_id, e.messages);
        });
        return;
      case "reaction":
        this.mutate((s) => {
          if (s.feed.channelId === e.channel_id) {
            mut.onReactionEvent(
              s.feed,
              e.seq,
              e.user_id,
              e.emoji,
              e.reactions,
              mut.selfIdOf(s.directory),
            );
          }
        });
        return;
      case "typing":
        this.mutate((s) => {
          if (s.feed.channelId === e.channel_id) {
            mut.onRemoteTyping(s.feed, e.user_id, mut.selfIdOf(s.directory), Date.now());
          }
        });
        return;
      case "read":
        // user_id is ignored: read marks only ever reach the reader's own
        // devices.
        this.applyReadMark(e.channel_id, e.last_read_seq);
        return;
      case "channel_created":
        this.mutate((s) => {
          mut.roomsOnCreated(
            s.rooms,
            e.channel_id,
            e.name,
            e.kind ?? "standard",
            e.peer_id ?? null,
            e.created_by,
            mut.selfIdOf(s.directory),
          );
        });
        return;
      case "channel_deleted":
        this.onChannelDeleted(e.channel_id);
        return;
      case "channel_renamed":
        this.mutate((s) => {
          mut.roomsOnUpdated(s.rooms, e);
          if (s.feed.channelId === e.channel_id) s.feed.channelName = e.name;
        });
        return;
      case "channel_settings":
        this.mutate((s) => {
          const row = mut.roomOf(s.rooms, e.channel_id);
          if (row) row.settings = e.settings;
        });
        return;
      case "membership": {
        // A pet came or went (pets-plan.md D360): the chat's `/` list is not
        // what it was. An account the directory has not heard of may be a
        // pet made a moment ago, so its frame counts too.
        const who = this.state.directory.people[e.user_id];
        this.mutate((s) => {
          mut.roomsOnMembership(
            s.rooms,
            e.channel_id,
            e.user_id,
            e.change,
            e.admin,
            mut.selfIdOf(s.directory),
          );
        });
        if (!who || isPet(who)) this.dropCommands([e.channel_id]);
        return;
      }
      case "profile_updated": {
        // Written inside the draft below, so declared through `as`.
        let moved = null as mut.PetMove | null;
        this.mutate((s) => {
          // The whole entry, a pet's three keys with it (D349): rebuilt
          // without them, the next frame about a pet would turn it back into
          // a person.
          moved = mut.applyDirectoryUpdate(s.directory, {
            user_id: e.user_id,
            username: e.username,
            display_name: e.display_name,
            bio: e.bio,
            avatar_file_id: e.avatar_file_id,
            created_at: e.created_at,
            bubble_light: e.bubble_light,
            bubble_dark: e.bubble_dark,
            kind: e.kind,
            pet_rev: e.pet_rev,
            retired: e.retired,
          });
          if (mut.isSelf(s.directory, e.user_id)) {
            s.selfUsername = e.username;
            s.account.displayName = e.display_name;
            s.account.avatarFileId = e.avatar_file_id;
            s.account.bubbleLight = e.bubble_light;
            s.account.bubbleDark = e.bubble_dark;
          }
          this.resyncNames(s);
        });
        if (moved) this.onPetsMoved([moved]);
        return;
      }
      case "pet_stopped":
        // Stop, or `/restart` taking it back (D368) — from any of the
        // reader's devices, this one included.
        this.mutate((s) => {
          mut.roomsOnPetStopped(s.rooms, e.channel_id, e.stopped);
        });
        return;
      case "call_started":
        this.onCallStarted(e);
        return;
      case "call_participants":
        this.mutate((s) => {
          mut.callsApply(s.calls, e);
          const row = mut.roomOf(s.rooms, e.channel_id);
          if (row) mut.setRoomCall(row, e.participants, s.directory);
        });
        return;
      case "call_ended": {
        const stopRing = this.state.ringing?.channelId === e.channel_id;
        this.mutate((s) => {
          mut.callsEnd(s.calls, e.channel_id);
          const row = mut.roomOf(s.rooms, e.channel_id);
          if (row) mut.setRoomCall(row, [], s.directory);
          if (s.ringing?.channelId === e.channel_id) {
            s.ringing = null;
          }
        });
        if (stopRing) this.onRing?.(false);
        return;
      }
      case "call_declined": {
        const stopRing = this.state.ringing?.channelId === e.channel_id;
        this.mutate((s) => {
          // Our own other device declined: stop ringing here too.
          if (s.ringing?.channelId === e.channel_id) {
            s.ringing = null;
          }
        });
        if (stopRing) this.onRing?.(false);
        return;
      }
      case "friends":
        this.onFriends(e);
        return;
      case "presence":
        this.onPresence(e.user_id, e.status);
        return;
      case "presence_sync":
        this.mutate((s) => {
          mut.applyPresenceSync(s.presence, e.presence);
        });
        void this.recheckCard();
        return;
      case "my_presence":
        // Only the manual half is folded — `status` is derived from manual
        // plus THIS machine's idleness, and this machine is the authority.
        this.mutate((s) => {
          s.account.manualStatus = normalizeManual(e.manual);
        });
        return;
      case "error":
        this.mutate((s) => {
          s.notice = e.message;
        });
        return;
      default:
        return;
    }
  }

  private onMessage(m: Message): void {
    const own = mut.isSelf(this.state.directory, m.sender_id);
    this.mutate((s) => {
      // A live frame is handed over the moment the message is stored: one
      // reading of the gateway's clock (D298).
      mut.noteClockSample(s.clock, m.sent_at_ms, Date.now());
      mut.roomsOnMessage(s.rooms, m, s.selectedChannel, own, Date.now(), this.t);
      if (s.feed.channelId === m.channel_id) {
        mut.reconcile(s.feed, stripTag(m), this.t);
        if (own) s.feed.lastReadSeq = Math.max(s.feed.lastReadSeq, m.seq);
        mut.resolveNames(s.feed, s.directory, this.t);
      }
    });
    // A sender the directory has never heard of: schedule ONE refresh (the
    // 30 s TTL is the flood gate), the desktop's nameOfTracked shape.
    if (!this.state.directory.people[m.sender_id]) {
      void this.refreshDirectory(false);
    }
    // The notification gate: own → no; service → no; visible open
    // channel → no. A hidden tab notifies even for the open channel —
    // hidden is hidden.
    if (own || (m.service && m.service.kind !== undefined)) return;
    // The tap (D341): every message but one the reader is looking at — the
    // open channel in a tab that is visible *and* focused, the same «in
    // front» the read marks go by. A visible tab behind another window is not
    // being looked at.
    if (!(this.canRead() && this.state.selectedChannel === m.channel_id)) {
      sounds.play("message");
    }
    const visibleHere = !document.hidden && this.state.selectedChannel === m.channel_id;
    if (visibleHere) return;
    if ("Notification" in window && Notification.permission === "granted") {
      const room = this.state.rooms.find((r) => r.channelId === m.channel_id);
      const sender = mut.nameOf(this.state.directory, m.sender_id);
      const title = room && room.kind !== "dm" ? `${sender} — ${room.name}` : sender;
      const n = new Notification(title, {
        // A notification is a string and cannot blur, so what a spoiler
        // hides goes as speckle (D343).
        body: maskedPreview(mut.preview(m, this.t)),
        tag: `echofloat-${m.channel_id}`,
      });
      n.onclick = () => {
        window.focus();
        this.openChannel(m.channel_id);
        n.close();
      };
    }
  }

  private onEdited(m: Message): void {
    this.mutate((s) => {
      if (s.feed.channelId === m.channel_id) {
        mut.reconcile(s.feed, stripTag(m), this.t);
        // A call widget grows a participant on every edit; the roster is
        // resolved in resolveNames.
        mut.resolveNames(s.feed, s.directory, this.t);
      }
      mut.roomsOnEdited(s.rooms, m, this.t);
    });
  }

  private onChannelDeleted(channelId: number): void {
    if (this.requestedChannel === channelId) this.cancelChannelNavigation();
    const wasSelected = this.state.selectedChannel === channelId;
    this.commandAsks.delete(channelId);
    this.mutate((s) => {
      const at = s.rooms.findIndex((r) => r.channelId === channelId);
      if (at !== -1) s.rooms.splice(at, 1);
      if (wasSelected) s.selectedChannel = null;
      mut.callsEnd(s.calls, channelId);
      delete s.commands[channelId];
    });
    if (wasSelected) {
      const next = this.state.rooms[0]?.channelId;
      if (next !== undefined) {
        void this.selectChannel(next);
      } else {
        this.mutate((s) => {
          mut.openFeed(s.feed, 0, "", 0, 0);
          s.feed.loading = false;
        });
      }
    }
  }

  private onCallStarted(call: import("../gen/dto").Call): void {
    let startRing = false;
    this.mutate((s) => {
      mut.callsApply(s.calls, call);
      const row = mut.roomOf(s.rooms, call.channel_id);
      if (row) mut.setRoomCall(row, call.participants, s.directory);
      // Ring gate: not our own start (another device is normal), and only
      // for channels we are a member of.
      const own = mut.isSelf(s.directory, call.started_by);
      if (own || !row?.member) return;
      s.ringing = {
        channelId: call.channel_id,
        starterName: mut.nameOf(s.directory, call.started_by),
        channelName: row.name,
        sinceMs: Date.now(),
      };
      startRing = true;
    });
    if (startRing) this.onRing?.(true);
  }

  private onFriends(e: import("../gen/dto").FriendsSnapshot): void {
    let news: mut.FriendNews[] = [];
    this.mutate((s) => {
      news = mut.applyFriendsSnapshot(s.friends, e);
    });
    // The two quiet notifications (D174): derived by diffing, never claimed
    // by the frame; the request one opens the sender's card.
    for (const item of news) {
      const name = mut.nameOf(this.state.directory, item.userId);
      this.pushToast(
        item.t === "requested" ? this.t.toastRequest(name) : this.t.toastAccepted(name),
        item.t === "requested" ? item.userId : null,
      );
    }
    void this.recheckCard();
  }

  private toastSeq = 0;

  private pushToast(text: string, userId: number | null): void {
    const lifetime = currentLifetime();
    const id = ++this.toastSeq;
    this.transition("toast", () => {
      this.mutate((s) => {
        s.toasts.push({ id, text, userId });
      });
    });
    const timer = setTimeout(() => {
      this.toastTimers.delete(timer);
      if (!isCurrent(lifetime)) return;
      this.transition("toast", () => {
        this.mutate((s) => {
          const at = s.toasts.findIndex((x) => x.id === id);
          if (at !== -1) s.toasts.splice(at, 1);
        });
      });
    }, 6000);
    this.toastTimers.add(timer);
  }

  dismissToast(id: number): void {
    this.transition("toast", () => {
      this.mutate((s) => {
        const at = s.toasts.findIndex((x) => x.id === id);
        if (at !== -1) s.toasts.splice(at, 1);
      });
    });
  }

  private onPresence(userId: number, status: import("../gen/dto").PublicStatus): void {
    const before =
      this.state.card.userId !== null
        ? mut.statusOf(this.state.presence, this.state.card.userId) !== null
        : null;
    this.mutate((s) => {
      mut.applyPresenceFrame(s.presence, userId, status);
    });
    const after =
      this.state.card.userId !== null
        ? mut.statusOf(this.state.presence, this.state.card.userId) !== null
        : null;
    // Only the here↔gone crossing re-reads the relation (D192/D193).
    if (this.state.card.userId !== null && before !== after) void this.recheckCard();
  }

  private async recheckCard(): Promise<void> {
    const lifetime = currentLifetime();
    const userId = this.state.card.userId;
    const generation = this.cardGeneration;
    if (userId === null) return;
    // A pet is nobody's friend (pets-plan.md D364): what its card shows is its
    // own card (D362), read fresh the same way — on a server that says it
    // knows pets; elsewhere it is drawn as a person, as §10 has it.
    if (this.state.account.pets !== null && isPet(this.state.directory.people[userId])) {
      try {
        const pet = await api.fetchPet(userId);
        if (!isCurrent(lifetime)) return;
        this.mutate((s) => {
          if (generation === this.cardGeneration && s.card.userId === userId) s.card.pet = pet;
        });
      } catch {
        // The card keeps what the directory says; a re-open retries.
      }
      return;
    }
    try {
      const relation = await api.fetchFriendship(userId);
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        if (generation === this.cardGeneration && s.card.userId === userId)
          s.card.relation = relation;
      });
    } catch {
      // The card keeps its placeholder; a re-open retries.
    }
  }

  // -------------------------------------------------------------------------
  // The resync (chat-api.md §3.3 + D160)

  async sync(): Promise<void> {
    const lifetime = currentLifetime();
    if (!session()) return;
    await this.refreshDirectory(true);
    if (!isCurrent(lifetime)) return;
    let envelope: import("../gen/dto").StateResponse;
    try {
      envelope = await api.fetchState();
      if (!isCurrent(lifetime)) return;
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.fail(e);
      return;
    }
    // Written inside the draft below, so declared through `as`: a plain
    // `= null` would narrow it to null for good after the callback.
    let ahead = null as { channel: number; seq: number } | null;
    this.mutate((s) => {
      s.notice = null;
      // The gateway's clock as it wrote this answer (D298), before anything
      // the answer carries: a client that has just started has heard no live
      // frame yet, and the rows below — and the page that follows them — are
      // already read through the offset. A gateway older than the field says
      // nothing, and the clocks are taken to agree until a frame says otherwise.
      if (typeof envelope.server_time_ms === "number") {
        mut.noteClockSample(s.clock, envelope.server_time_ms, Date.now());
      }
      // Friends BEFORE rooms: a just-appeared DM row must already have a
      // friendship behind it when the Friends tab counts.
      mut.applyFriendsSnapshot(s.friends, envelope.friends);
      mut.applyPresenceState(s.presence, envelope.presence ?? null);
      mut.applyState(s.rooms, envelope.channels, s.directory, Date.now(), this.t);
      if (s.selectedChannel !== null && !mut.roomOf(s.rooms, s.selectedChannel)) {
        s.selectedChannel = null;
      }
      const open = s.selectedChannel;
      if (open !== null) {
        const row = mut.roomOf(s.rooms, open);
        if (row) {
          // Below the new ceiling the window cannot tell messages deleted
          // while we were away from messages it missed: loadNewer() below
          // asks, and its answer is what says the window holds the end again.
          mut.resyncCeiling(s.feed, row.maxSeq);
          s.feed.lastReadSeq = Math.max(s.feed.lastReadSeq, row.lastReadSeq);
          // And the other way round (D346): a mark the window holds past the
          // gateway's is one the gateway was never told — the feed counts
          // your own message read on arrival, and only a posted mark reaches
          // it. Taking the gateway's word drew a badge over a feed scrolled
          // to its end, which nothing would post past. The row takes the
          // window's mark, and the gateway is told right after.
          if (s.feed.channelId === open && s.feed.lastReadSeq > row.lastReadSeq) {
            mut.roomsOnRead(s.rooms, open, s.feed.lastReadSeq);
            ahead = { channel: open, seq: s.feed.lastReadSeq };
          }
        }
      }
    });
    if (ahead) void this.markReadAt(ahead.channel, ahead.seq);

    // A URL coordinator may have requested an open synchronously from the
    // store notification above while its visual commit is still deferred.
    // Resync must not replace that navigation with the first/previous room.
    if (this.requestedChannel !== null) return;
    const open = this.state.selectedChannel;
    if (open !== null) {
      const newest = mut.newestSeq(this.state.feed) ?? 0;
      if (this.state.feed.maxSeq - newest > WINDOW_SIZE) {
        // The whole window is stale: reopening lands on the bottom in two
        // requests instead of dozens trimHead would throw away.
        await this.selectChannel(open, true);
        if (!isCurrent(lifetime)) return;
      } else {
        await this.loadNewer();
        if (!isCurrent(lifetime)) return;
        await this.refreshWindowReactions();
        if (!isCurrent(lifetime)) return;
      }
    } else {
      const first = this.state.rooms[0]?.channelId;
      if (first !== undefined) await this.selectChannel(first);
      if (!isCurrent(lifetime)) return;
    }
  }

  private async refreshDirectory(force: boolean): Promise<void> {
    const lifetime = currentLifetime();
    const dir = this.state.directory;
    const now = Date.now();
    if (!force && dir.lastRefreshMs !== null && now - dir.lastRefreshMs < 30_000) return;
    try {
      const entries = await api.fetchDirectory();
      if (!isCurrent(lifetime)) return;
      let moved: mut.PetMove[] = [];
      this.mutate((s) => {
        moved = mut.applyDirectory(s.directory, entries, s.selfUsername);
        s.directory.lastRefreshMs = now;
        s.directory.refreshWanted = false;
        this.resyncNames(s);
      });
      this.onPetsMoved(moved);
    } catch {
      // A directory that failed to refresh costs names, not data.
    }
  }

  /** Pets whose cards moved (pets-plan.md D360, D362): every `/` list read
   * against an older one is asked for again, and so is the card open on one
   * of them — which is also how a card opened before the directory knew its
   * account for a pet becomes a pet's card. */
  private onPetsMoved(moved: readonly mut.PetMove[]): void {
    if (moved.length === 0) return;
    const stale = new Set<number>();
    for (const move of moved) {
      for (const channel of mut.staleCommandLists(this.state.commands, move)) stale.add(channel);
    }
    this.dropCommands([...stale]);
    const card = this.state.card.userId;
    if (card !== null && moved.some((move) => move.petId === card)) void this.recheckCard();
  }

  /** The one function that rewrites every directory-fed cache. */
  private resyncNames(s: UiState): void {
    mut.resolveNames(s.feed, s.directory, this.t);
    for (const row of s.rooms) {
      if (row.callMemberIds.length > 0) {
        mut.setRoomCall(row, row.callMemberIds, s.directory);
      }
    }
  }

  private fail(e: unknown): void {
    if (e instanceof api.ApiError && e.unauthorized) {
      if (!inReauthWindow()) void this.logout();
      return;
    }
    this.mutate((s) => {
      s.notice = e instanceof Error ? e.message : String(e);
    });
  }

  // -------------------------------------------------------------------------
  // Channel lifecycle

  openChannel(id: number): void {
    void this.selectChannel(id);
  }

  /** Invalidate an abandoned route even if its transition has not committed yet. */
  cancelChannelNavigation(nextTarget?: number | null): void {
    // URL feedback for a runtime command acknowledges the same in-flight open;
    // invalidating it here would strand the loading feed without its response.
    if (nextTarget != null && nextTarget === this.requestedChannel) return;
    this.channelGeneration += 1;
    this.requestedChannel = null;
  }

  async selectChannel(id: number, force = false): Promise<void> {
    const lifetime = currentLifetime();
    if (!this.state.rooms.some((r) => r.channelId === id)) return;
    if (
      !force &&
      (this.requestedChannel === id ||
        (this.requestedChannel === null &&
          this.state.selectedChannel === id &&
          this.state.feed.channelId === id &&
          !this.state.feed.loading))
    )
      return;
    const generation = ++this.channelGeneration;
    this.requestedChannel = id;
    const current = () => isCurrent(lifetime) && generation === this.channelGeneration;
    // The host may defer its commit. Wait for it before reading the resulting
    // feed, and also check ownership *inside* it: A -> B -> A is three opens.
    await new Promise<void>((resolve) => {
      // A host may suppress its deferred callback when the account ends. The
      // caller must still settle instead of waiting forever for an old frame.
      const finish = () => {
        lifetime.signal.removeEventListener("abort", finish);
        resolve();
      };
      lifetime.signal.addEventListener("abort", finish, { once: true });
      this.host.transition("center", () => {
        try {
          if (current()) {
            this.mutate((s) => {
              const row = s.rooms.find((r) => r.channelId === id);
              if (!row) return;
              s.selectedChannel = id;
              mut.openFeed(s.feed, id, row.name, row.maxSeq, row.lastReadSeq);
            });
          }
        } finally {
          finish();
        }
      });
    });
    if (!current() || this.state.feed.channelId !== id) return;
    const { maxSeq, lastReadSeq } = this.state.feed;
    const unread = maxSeq > lastReadSeq && lastReadSeq > 0;
    try {
      const head = unread
        ? await api.fetchMessages(id, { before: lastReadSeq + 1, limit: 25 })
        : await api.fetchMessages(id, { limit: PAGE_SIZE });
      if (!current()) return;
      const tail = unread
        ? await api.fetchMessages(id, { after: lastReadSeq, limit: PAGE_SIZE })
        : { messages: [] as Message[] };
      if (!current()) return;
      const reachedStart = head.messages.length === 0 || (head.messages[0]?.seq ?? 0) <= 1;
      this.transition("center", () => {
        if (!current() || this.state.feed.channelId !== id) return;
        this.requestedChannel = null;
        this.mutate((s) => {
          s.feed.loading = false;
          splicePage(s.feed, head, true, this.t);
          splicePage(s.feed, tail as import("../gen/dto").PageResponse, true, this.t);
          s.feed.reachedStart = reachedStart;
          // The newest page is the end by definition; the page past the mark
          // is the end when it came back short. Either way only if nothing
          // arrived past it while it was in flight (D277).
          s.feed.reachedEnd = mut.endHolds(
            s.feed,
            !unread || tail.messages.length < PAGE_SIZE,
            maxSeq,
          );
          mut.insertUnreadDivider(s.feed);
          mut.resolveNames(s.feed, s.directory, this.t);
        });
      });
    } catch (e) {
      if (!current() || this.state.feed.channelId !== id) return;
      this.requestedChannel = null;
      this.mutate((s) => {
        s.feed.loading = false;
      });
      this.fail(e);
    }
  }

  async loadOlder(): Promise<void> {
    const lifetime = currentLifetime();
    const generation = this.channelGeneration;
    const feed = this.state.feed;
    if (feed.loadingOlder || feed.reachedStart || feed.bubbles.length === 0) return;
    const oldest = mut.oldestSeq(feed);
    if (oldest === undefined) return;
    const channel = feed.channelId;
    this.mutate((s) => {
      s.feed.loadingOlder = true;
    });
    try {
      const page = await api.fetchMessages(channel, {
        before: oldest,
        limit: PAGE_SIZE,
      });
      if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
      if (this.state.feed.channelId !== channel) return; // only the setter clears the flag
      this.mutate((s) => {
        s.feed.loadingOlder = false;
        if (page.messages.length === 0) {
          s.feed.reachedStart = true;
        } else {
          splicePage(s.feed, page, false, this.t);
          mut.trimTail(s.feed);
          mut.resolveNames(s.feed, s.directory, this.t);
        }
      });
    } catch (e) {
      if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
      if (this.state.feed.channelId !== channel) return;
      this.mutate((s) => {
        s.feed.loadingOlder = false;
        s.notice = e instanceof Error ? e.message : String(e);
      });
    }
  }

  /** The ?after= gap-fill — a loop, not one page: after a reconnect the
   * ceiling can be hundreds of seqs above the window. */
  private async loadNewer(): Promise<void> {
    const lifetime = currentLifetime();
    const generation = this.channelGeneration;
    for (;;) {
      const newest = mut.newestSeq(this.state.feed);
      const askedAt = this.state.feed.maxSeq;
      if ((newest ?? 0) >= askedAt) return;
      const channel = this.state.feed.channelId;
      if (channel === 0) return;
      let page: import("../gen/dto").PageResponse;
      try {
        page = await api.fetchMessages(
          channel,
          newest === undefined ? { limit: PAGE_SIZE } : { after: newest, limit: PAGE_SIZE },
        );
        if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
      } catch (e) {
        if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
        this.fail(e);
        return;
      }
      const short = page.messages.length < PAGE_SIZE;
      if (this.state.feed.channelId !== channel) return;
      this.mutate((s) => {
        if (newest === undefined) {
          // That was the NEWEST page, not a continuation.
          s.feed.reachedStart = page.messages.length === 0 || (page.messages[0]?.seq ?? 0) <= 1;
        }
        splicePage(s.feed, page, true, this.t);
        mut.trimHead(s.feed);
        mut.resolveNames(s.feed, s.directory, this.t);
        // A short page is the channel's end — a ceiling above it is holes
        // that deletions left, which is what lets the mark reach it (D277).
        if (short) s.feed.reachedEnd = mut.endHolds(s.feed, true, askedAt);
      });
      if (short) return;
    }
  }

  /** D160: `?after=` closed the message gap; the chips of what was already
   * on screen are the one thing it cannot heal. Best-effort. */
  private async refreshWindowReactions(): Promise<void> {
    const lifetime = currentLifetime();
    const generation = this.channelGeneration;
    const feed = this.state.feed;
    const oldest = mut.oldestSeq(feed);
    const ceiling = mut.newestSeq(feed);
    if (oldest === undefined || ceiling === undefined) return;
    let cursor = Math.max(0, oldest - 1);
    const channel = feed.channelId;
    while (cursor < ceiling) {
      let page: import("../gen/dto").PageResponse;
      try {
        page = await api.fetchMessages(channel, {
          after: cursor,
          limit: PAGE_SIZE * 2,
        });
        if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
      } catch {
        if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
        return; // a failure costs stale decoration, not data
      }
      const last = page.messages.at(-1)?.seq;
      if (last === undefined) return;
      const short = page.messages.length < PAGE_SIZE * 2;
      if (this.state.feed.channelId !== channel) return;
      this.mutate((s) => {
        splicePage(s.feed, page, true, this.t);
      });
      cursor = last;
      if (short) return;
    }
  }

  // -------------------------------------------------------------------------
  // Sending

  /** Every keystroke calls this; the throttle mutates regardless of channel
   * (the state lives on the feed, so switching channels resets it). */
  noteTyping(): void {
    let due = false;
    this.mutate((s) => {
      due = mut.shouldSendTyping(s.feed, Date.now());
    });
    const channel = this.state.feed.channelId;
    if (due && channel !== 0) this.socket?.sendTyping(channel);
  }

  send(text: string, staged: StagedFile[] = [], options: SendOptions = {}): void {
    if (text.trim() === "" && staged.length === 0) return;
    this.prepareSend()?.(text, staged, options);
  }

  /** Snapshot the user's destination before any async attachment preparation. */
  prepareSend(): PreparedSend | null {
    const s = this.state;
    const channel = s.feed.channelId;
    if (channel === 0 || !session()) return null;
    const row = s.rooms.find((r) => r.channelId === channel);
    const accountId = mut.selfIdOf(s.directory);
    const composerRevision = s.feed.composerRevision;
    const mode = s.feed.mode;
    const context: SendContext = Object.freeze({
      lifetime: currentLifetime(),
      accountId,
      selfName: mut.nameOf(s.directory, accountId),
      channel,
      replyTo: s.feed.mode.t === "replying" ? s.feed.mode.seq : null,
      editing: s.feed.mode.t === "editing" ? s.feed.mode.seq : null,
      nonce: crypto.randomUUID(),
      maxChars: row?.settings.max_message_chars ?? 4096,
      unlimited: s.account.isAdmin,
      pause: row?.admin ? 0 : (row?.settings.slowmode_s ?? 0),
      // The channel's clock as it stands at the gesture (D298), whatever is
      // being sent — words, files, a voice note. An edit takes none (D296).
      ttlS: s.feed.mode.t === "editing" ? null : (s.sendTtl[channel] ?? null),
    });
    // Capture now, consume only once a real message is queued. A discarded
    // recording keeps the reply/edit; a newer composition is never cleared.
    let consumed = false;
    return (text, staged = [], options = {}) => {
      if (consumed || !isCurrent(context.lifetime)) return;
      if (!this.enqueueSend(context, text, staged, options.noLinkPreview === true)) return;
      consumed = true;
      if (
        this.state.feed.channelId === channel &&
        this.state.feed.composerRevision === composerRevision &&
        this.state.feed.mode === mode
      ) {
        this.cancelMode();
      }
    };
  }

  private enqueueSend(
    context: SendContext,
    text: string,
    staged: StagedFile[],
    noLinkPreview: boolean,
  ): boolean {
    // With everybody the directory knows by handle, so an `@username` —
    // picked from the composer's list or typed by hand — leaves as a mention,
    // and an edit's words become mentions again on save (D284).
    const parsed = parseMarkdown(text, mut.handlesOf(this.state.directory));
    if (parsed.text.trim() === "" && staged.length === 0) return false;
    const limit = Math.min(staged.length > 0 ? 1024 : 4096, context.maxChars);
    const split =
      context.editing === null
        ? splitMessage(parsed.text, parsed.entities, limit)
        : { parts: [parsed], truncated: false };
    if (split.parts.length === 0) return false;
    if (split.truncated) this.showNotice(this.t.messageTooLongSplit(MAX_SPLIT_PARTS));
    const intent: SendIntent = Object.freeze({
      ...context,
      noLinkPreview,
      parts: Object.freeze(
        split.parts.map((part, index) =>
          Object.freeze({
            ...part,
            nonce: index === 0 ? context.nonce : crypto.randomUUID(),
          }),
        ),
      ),
      staged: Object.freeze(staged.map((item) => Object.freeze({ ...item }))),
    });
    this.sendQueue.push(intent);
    void this.drainSends();
    return true;
  }

  private async drainSends(): Promise<void> {
    const lifetime = currentLifetime();
    if (this.sending) return;
    this.sending = true;
    try {
      for (;;) {
        const intent = this.sendQueue.shift();
        if (!intent) return;
        if (!isCurrent(intent.lifetime)) continue;
        await this.sendNow(intent);
        if (!isCurrent(lifetime)) return;
      }
    } catch (error) {
      if (isCurrent(lifetime)) this.fail(error);
    } finally {
      if (isCurrent(lifetime)) {
        this.sending = false;
      }
    }
  }

  private async sendNow(intent: SendIntent): Promise<void> {
    const lifetime = currentLifetime();
    if (!isCurrent(intent.lifetime)) return;
    const { channel, editing, replyTo, staged, accountId: selfId, selfName } = intent;
    const { noLinkPreview, ttlS } = intent;
    const [first, ...tail] = intent.parts;
    if (editing !== null) {
      await this.commitEdit(channel, editing, first!.text, first!.entities, noLinkPreview);
      if (!isCurrent(lifetime)) return;
      return;
    }
    const nonce = first!.nonce;
    const sentAt = Date.now();
    const optimistic: Message = {
      channel_id: channel,
      seq: PENDING_SEQ,
      sender_id: selfId,
      sent_at_ms: sentAt,
      nonce,
      reply_to_seq: replyTo,
      text: first!.text,
      entities: first!.entities,
      attachments: [],
      ...(noLinkPreview ? { no_link_preview: true } : {}),
      ...optimisticTtl(ttlS),
    } as unknown as Message;

    this.mutate((st) => {
      if (st.feed.channelId !== channel) return;
      const b = mut.newBubble(optimistic, selfName, true);
      // The quote the gateway will answer with (D273), taken now from the
      // message being answered, so the header has its words while the send
      // is in flight.
      const target =
        replyTo === null ? undefined : st.feed.bubbles.find((x) => x.message.seq === replyTo);
      if (target) b.message.reply_to = mut.quoteOf(target.message);
      b.state = staged.length > 0 ? "preparing" : "sending";
      b.localPreviews = staged.map((f) => f.previewUrl).filter((u): u is string => !!u);
      st.feed.bubbles.push(b); // PENDING_SEQ sorts it last by construction
      mut.regroup(st.feed);
      mut.resolveNames(st.feed, st.directory, this.t);
    });

    // Attachments first (chat-api.md §2.6): sha-dedup, 1 MiB chunks, per-file
    // progress folded onto the bubble by nonce.
    const refs: import("../gen/dto").AttachmentInput[] = [];
    for (let i = 0; i < staged.length; i += 1) {
      const item = staged[i]!;
      const onProgress = (fraction: number) => {
        if (!isCurrent(intent.lifetime)) return;
        this.mutate((st) => {
          const b = st.feed.bubbles.find((x) => x.message.nonce === nonce);
          if (b) b.sendProgress = (i + fraction) / staged.length;
        });
      };
      try {
        const sendKind = sendKindOf(item);
        // A picture goes as two weights (D233): the 15 KiB thumbnail first,
        // so the far side has something to draw a second after «send», then
        // the 300 KiB preview that becomes the attachment itself.
        if (sendKind === "photo" && photoWeightsSupported()) {
          const { preview, thumb } = await photoWeights(item.file);
          if (!isCurrent(lifetime)) return;
          const thumbUp = await upload(
            thumb.blob,
            "photo",
            { width: thumb.width, height: thumb.height },
            (f) => onProgress(f * THUMB_SHARE),
          );
          if (!isCurrent(lifetime)) return;
          const previewUp = await upload(
            preview.blob,
            "photo",
            {
              fileName: item.file.name,
              width: preview.width,
              height: preview.height,
            },
            (f) => onProgress(THUMB_SHARE + f * (1 - THUMB_SHARE)),
          );
          if (!isCurrent(lifetime)) return;
          refs.push({
            file_id: previewUp.file_id,
            thumb_file_id: thumbUp.file_id,
            ...(item.file.name !== "" ? { file_name: item.file.name } : {}),
            ...(item.spoiler ? { spoiler: true } : {}),
          });
          continue;
        }
        // A picture that took the file door is still a picture: the budget
        // that applies is the original's, not the 100 MiB an installer gets.
        if (item.kind === "photo" && item.file.size > ORIGINAL_MAX_BYTES) {
          throw new UploadError(0, this.t.originalOverLimit(ORIGINAL_MAX_BYTES));
        }
        const probed = await probeFile(item, lifetime);
        if (!isCurrent(lifetime)) return;
        const result = await upload(
          item.file,
          sendKind,
          {
            fileName: item.file.name,
            ...(probed.width !== undefined ? { width: probed.width } : {}),
            ...(probed.height !== undefined ? { height: probed.height } : {}),
            ...(probed.durationMs !== undefined ? { durationMs: probed.durationMs } : {}),
          },
          onProgress,
          { unlimited: sendKind === "file" && intent.unlimited },
        );
        if (!isCurrent(lifetime)) return;
        refs.push({
          file_id: result.file_id,
          ...(item.file.name !== "" ? { file_name: item.file.name } : {}),
          ...(item.waveform !== undefined ? { waveform: item.waveform } : {}),
          ...(item.spoiler ? { spoiler: true } : {}),
        });
      } catch (e) {
        if (!isCurrent(lifetime)) return;
        this.strandTail(channel, tail, noLinkPreview, ttlS);
        this.markFailed(nonce, e instanceof Error ? e.message : String(e));
        return;
      }
    }

    this.mutate((st) => {
      const b = st.feed.bubbles.find((x) => x.message.nonce === nonce);
      if (b) b.state = "sending";
    });

    // The flag only when it is set, as serde's `skip_serializing_if` writes it:
    // a message that keeps its card says nothing about it. The lifetime the
    // same way (D296): a message that stays says nothing about that either.
    const body: SendBody = {
      nonce,
      text: first!.text,
      entities: first!.entities,
      attachments: refs,
      ...(replyTo !== null ? { reply_to_seq: replyTo } : {}),
      ...(noLinkPreview ? { no_link_preview: true } : {}),
      ...(ttlS !== null ? { ttl_s: ttlS } : {}),
    };

    try {
      const server = await api.sendMessage(channel, body);
      if (!isCurrent(lifetime)) return;
      this.noteServerClock(server);
      if (this.state.feed.channelId === channel) {
        this.mutate((st) => {
          mut.reconcile(st.feed, server, this.t);
          mut.resolveNames(st.feed, st.directory, this.t);
        });
      }
      // A gateway older than `ttl_s` ignores it, and says so only by answering
      // without one (D296): the sender believes these words will go, and has to
      // be told that they will not. Said once per send, over its first part —
      // the rest go to the same gateway, and under slow mode would raise the
      // same line again every pause. Read off `ttl_s` and not off the deadline,
      // which a gateway that *does* support this leaves empty until somebody
      // reads the message (D302).
      if (ttlS !== null && server.ttl_s == null) {
        this.showNotice(this.t.ttlUnsupported());
      }
      await this.markReadAt(channel, server.seq);
      if (!isCurrent(lifetime)) return;
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.strandTail(channel, tail, noLinkPreview, ttlS);
      this.markFailed(nonce, e instanceof Error ? e.message : String(e));
      return;
    }

    // The tail, sequentially: seq is what the feed sorts on. Slow mode is
    // not bypassed for admins server-side exemption — the row knows.
    const pause = intent.pause;
    for (const part of tail) {
      if (pause > 0) await sessionDelay(pause * 1000, intent.lifetime);
      if (!isCurrent(lifetime)) return;
      const ok = await this.sendPart(intent, part);
      if (!isCurrent(lifetime)) return;
      if (!ok) {
        const rest = tail.slice(tail.indexOf(part) + 1);
        this.strandTail(channel, rest, noLinkPreview, ttlS);
        return;
      }
    }
  }

  private async sendPart(intent: SendIntent, part: SendIntent["parts"][number]): Promise<boolean> {
    const lifetime = currentLifetime();
    if (!isCurrent(intent.lifetime)) return false;
    const { channel, accountId: selfId, selfName, noLinkPreview, ttlS } = intent;
    const { text, entities, nonce } = part;
    const sentAt = Date.now();
    const optimistic: Message = {
      channel_id: channel,
      seq: PENDING_SEQ,
      sender_id: selfId,
      sent_at_ms: sentAt,
      nonce,
      text,
      entities,
      attachments: [],
      ...(noLinkPreview ? { no_link_preview: true } : {}),
      ...optimisticTtl(ttlS),
    } as unknown as Message;
    if (this.state.feed.channelId === channel) {
      this.mutate((st) => {
        const b = mut.newBubble(optimistic, selfName, true);
        b.state = "sending";
        st.feed.bubbles.push(b);
        mut.regroup(st.feed);
      });
    }
    try {
      const server = await api.sendMessage(channel, {
        nonce,
        text,
        entities,
        attachments: [],
        ...(noLinkPreview ? { no_link_preview: true } : {}),
        ...(ttlS !== null ? { ttl_s: ttlS } : {}),
      });
      if (!isCurrent(lifetime)) return false;
      this.noteServerClock(server);
      if (this.state.feed.channelId === channel) {
        this.mutate((st) => {
          mut.reconcile(st.feed, server, this.t);
          mut.resolveNames(st.feed, st.directory, this.t);
        });
      }
      await this.markReadAt(channel, server.seq);
      if (!isCurrent(lifetime)) return false;
      return true;
    } catch (e) {
      if (!isCurrent(lifetime)) return false;
      this.markFailed(nonce, e instanceof Error ? e.message : String(e));
      return false;
    }
  }

  /** A failed bubble stays in the feed with its text intact — the composer
   * already cleared itself, so the feed is the only place the text survives.
   * There is no automatic retry. */
  private markFailed(nonce: string, message: string): void {
    this.mutate((s) => {
      const b = s.feed.bubbles.find((x) => x.message.nonce === nonce);
      if (b) b.state = "failed";
      s.notice = message;
    });
  }

  /** Never post past a hole: unsent parts become visible failed bubbles. */
  private strandTail(
    channel: number,
    parts: Array<{ text: string; entities: import("../gen/dto").Entity[] }>,
    noLinkPreview: boolean,
    ttlS: number | null,
  ): void {
    if (parts.length === 0) return;
    if (this.state.feed.channelId !== channel) return; // never paint another channel
    const selfId = mut.selfIdOf(this.state.directory);
    const selfName = mut.nameOf(this.state.directory, selfId);
    this.mutate((s) => {
      for (const part of parts) {
        const sentAt = Date.now();
        const m: Message = {
          channel_id: channel,
          seq: PENDING_SEQ,
          sender_id: selfId,
          sent_at_ms: sentAt,
          nonce: crypto.randomUUID(),
          text: part.text,
          entities: part.entities,
          attachments: [],
          ...(noLinkPreview ? { no_link_preview: true } : {}),
          ...optimisticTtl(ttlS),
        } as unknown as Message;
        const b = mut.newBubble(m, selfName, true);
        b.state = "failed";
        s.feed.bubbles.push(b);
      }
      mut.regroup(s.feed);
    });
  }

  private async commitEdit(
    channel: number,
    seq: number,
    text: string,
    entities: import("../gen/dto").Entity[],
    noLinkPreview: boolean,
  ): Promise<void> {
    const lifetime = currentLifetime();
    try {
      // Always said, `false` included (D289): to the gateway an edit without
      // the field leaves the flag as it was — an older client's edit must not
      // bring back a card its author closed — so a card reopened here has to
      // be asked for out loud.
      const server = await api.editMessage(channel, seq, {
        text,
        entities,
        no_link_preview: noLinkPreview,
      });
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        if (s.feed.channelId !== channel) return;
        // Every reply quoting this message still carries the pre-edit words
        // (D273), and the author must not be shown them while the socket echo
        // is still in flight.
        mut.requote(s.feed, server);
        const b = s.feed.bubbles.find((x) => x.message.seq === seq);
        if (b) b.message = server;
        mut.resolveNames(s.feed, s.directory, this.t);
      });
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.fail(e);
    }
  }

  beginReply(seq: number): void {
    this.mutate((s) => {
      const b = s.feed.bubbles.find((x) => x.message.seq === seq);
      if (!b) return;
      s.feed.mode = { t: "replying", seq };
      s.feed.composerRevision += 1;
    });
  }

  beginEdit(seq: number): void {
    this.mutate((s) => {
      const b = s.feed.bubbles.find((x) => x.message.seq === seq);
      if (!b || !b.isOwn) return;
      s.feed.mode = { t: "editing", seq };
      s.feed.composerRevision += 1;
    });
  }

  cancelMode(): void {
    this.mutate((s) => {
      s.feed.mode = { t: "new" };
      s.feed.composerRevision += 1;
    });
  }

  async deleteMessage(seq: number): Promise<void> {
    const lifetime = currentLifetime();
    const channel = this.state.feed.channelId;
    if (channel === 0) return;
    try {
      await api.deleteMessage(channel, seq);
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        const open = s.feed.channelId === channel;
        if (open) mut.applyDeletion(s.feed, seq, this.t);
        const replacement = open && s.feed.reachedEnd ? mut.newestMessage(s.feed) : null;
        mut.roomsOnDeleted(
          s.rooms,
          channel,
          seq,
          replacement,
          s.selectedChannel,
          Date.now(),
          this.t,
        );
      });
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.fail(e);
    }
  }

  // -------------------------------------------------------------------------
  // Disappearing messages (docs/disappearing-messages.md D296–D298)

  /** The composer's clock: the lifetime whatever is sent to `channelId` next
   * goes out under, or null to turn it off. Sticky per channel and for the
   * session (D298) — `prepareSend` reads it at every send gesture. A lifetime
   * outside D296's range is not taken: the gateway refuses the send it would
   * ride on rather than clamp it, so the dialog's gate is repeated here. */
  setSendTtl(channelId: number, ttlS: number | null): void {
    if (ttlS !== null && !isValidTtl(ttlS)) return;
    this.mutate((s) => {
      if (ttlS === null) delete s.sendTtl[channelId];
      else s.sendTtl[channelId] = ttlS;
    });
  }

  /** The gateway's now, as near as this client can tell (D298): what a
   * deadline is read against, here and in a bubble's countdown. */
  serverNow(): number {
    return mut.serverNow(this.state.clock, Date.now());
  }

  /** The answer to a send of ours is the gateway's clock read a moment ago,
   * as a live frame is (`onMessage`). Before the answer is folded in, so that
   * its deadline is read against the offset it has just corrected. */
  private noteServerClock(answer: Message): void {
    this.mutate((s) => {
      mut.noteClockSample(s.clock, answer.sent_at_ms, Date.now());
    });
  }

  /** A message is gone: the one way out of this client's state (D253), taken
   * by the gateway's `message_deleted` and by a deadline reached on our own
   * clock alike (D298) — in either order and any number of times. The window
   * drops a row it holds and is quiet about one it does not; the sidebar's
   * line stops quoting it once, because it then quotes something else or
   * nothing; and it is counted as a hole once (`roomsOnDeleted`). */
  private forget(s: UiState, channelId: number, seq: number): void {
    const open = s.feed.channelId === channelId;
    if (open) mut.applyDeletion(s.feed, seq, this.t);
    // The sidebar quotes the newest message of every room, open or not, and
    // has to stop quoting one that is gone. What is newest now only a window
    // holding the channel's end can say — and only among what the server has
    // confirmed.
    const replacement = open && s.feed.reachedEnd ? mut.newestMessage(s.feed) : null;
    mut.roomsOnDeleted(s.rooms, channelId, seq, replacement, s.selectedChannel, Date.now(), this.t);
  }

  /** `state` less every copy of a message past its deadline on the gateway's
   * clock — each through `forget`, exactly as its `message_deleted` would
   * take it and will again, harmlessly, when it comes. The server's frame is
   * what normally removes a bubble; this is for when it cannot arrive — a
   * dead socket, a suspended laptop — because a copy that outlives its
   * deadline on a screen is the one thing its sender was promised would not
   * happen. The same state back when nothing is due. */
  private withoutExpired(state: UiState): UiState {
    const now = mut.serverNow(state.clock, Date.now());
    const due = mut.dueMessages(state.feed, state.rooms, now);
    if (due.length === 0) return state;
    return produce(state, (s) => {
      for (const m of due) this.forget(s, m.channelId, m.seq);
    });
  }

  /** Lets go of what is due now and stands the timer for what is due next. */
  private expireDue(): void {
    const next = this.withoutExpired(this.state);
    if (next !== this.state) this.store.setState(next, true);
    this.armExpiry();
  }

  /** One timer, for the nearest deadline held. `mutate` stands it again
   * whenever the set of deadlines or the clock's offset may have moved. */
  private armExpiry(): void {
    this.stopExpiry();
    if (this.disposed) return;
    const deadline = mut.nextDeadline(this.state.feed, this.state.rooms);
    if (deadline === null) return;
    const lifetime = currentLifetime();
    this.expiryTimer = setTimeout(
      () => {
        this.expiryTimer = null;
        if (isCurrent(lifetime)) this.expireDue();
      },
      Math.max(0, deadline - this.serverNow()),
    );
  }

  private stopExpiry(): void {
    if (this.expiryTimer !== null) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  // -------------------------------------------------------------------------
  // Reactions (D145–D167)

  async toggleReaction(seq: number, emoji: string): Promise<void> {
    const lifetime = currentLifetime();
    const s = this.state;
    const channel = s.feed.channelId;
    if (channel === 0) return;
    const b = s.feed.bubbles.find((x) => x.message.seq === seq);
    if (!b) return;
    const selfId = mut.selfIdOf(s.directory);
    const next = b.myReaction === emoji ? null : emoji; // picking the set emoji clears it
    let previous: string | null = null;
    this.mutate((st) => {
      previous = mut.setMyReaction(st.feed, seq, next, selfId);
    });
    try {
      const answer = next
        ? await api.setReaction(channel, seq, next)
        : await api.clearReaction(channel, seq);
      if (!isCurrent(lifetime)) return;
      if (this.state.feed.channelId === channel) {
        this.mutate((st) => {
          mut.applyReactions(st.feed, seq, answer.reactions);
          const bb = st.feed.bubbles.find((x) => x.message.seq === seq);
          if (bb) bb.myReaction = answer.my_reaction ?? null;
        });
      }
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      if (this.state.feed.channelId === channel) {
        this.mutate((st) => {
          mut.setMyReaction(st.feed, seq, previous, selfId);
        });
      }
      this.mutate((st) => {
        st.notice = e instanceof Error ? e.message : String(e);
      });
    }
  }

  // -------------------------------------------------------------------------
  // Read marks

  /** The reader reached the bottom (scroll spam calls this; the divider
   * check keeps the repaints away). */
  markRead(): void {
    if (!this.canRead()) return;
    if (this.state.feed.dividerBefore !== null) {
      this.mutate((s) => {
        mut.dismissUnreadDivider(s.feed);
      });
    }
    const seq = mut.readableSeq(this.state.feed);
    if (seq === null) return;
    void this.markReadAt(this.state.feed.channelId, seq);
  }

  /** A message has been wholly on screen while the tab was in front (D249):
   * the badge comes down as messages pass under the eye instead of waiting for
   * the bottom. The unread divider stays — reaching the end is what takes that
   * down, and that goes through `markRead` above. */
  markReadUpTo(seen: number): void {
    if (!this.canRead()) return;
    const seq = mut.readableSeqUpTo(this.state.feed, seen);
    if (seq === null) return;
    void this.markReadAt(this.state.feed.channelId, seq);
  }

  private canRead(): boolean {
    return !document.hidden && (typeof document.hasFocus !== "function" || document.hasFocus());
  }

  private async markReadAt(channel: number, seq: number): Promise<void> {
    const lifetime = currentLifetime();
    if (seq === PENDING_SEQ) return; // must never be posted (poisons the mark)
    try {
      const { last_read_seq } = await api.markRead(channel, seq);
      if (!isCurrent(lifetime)) return;
      this.applyReadMark(channel, last_read_seq);
    } catch {
      if (!isCurrent(lifetime)) return;
      // A lost read mark costs a badge, not data.
    }
  }

  private applyReadMark(channel: number, effective: number): void {
    this.mutate((s) => {
      mut.roomsOnRead(s.rooms, channel, effective);
      if (s.feed.channelId === channel && s.feed.lastReadSeq < effective) {
        s.feed.lastReadSeq = effective; // never backwards
      }
    });
  }

  // -------------------------------------------------------------------------
  // Join / membership (D114)

  /** The bare door. Nothing rides with it since D295: a private channel is
   * entered by its invite instead, through {@link joinByToken}. */
  async join(channelId: number): Promise<void> {
    const lifetime = currentLifetime();
    this.mutate((s) => {
      const row = mut.roomOf(s.rooms, channelId);
      if (row) row.joining = true;
    });
    try {
      const joined = await api.joinChannel(channelId);
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        mut.roomsOnJoined(s.rooms, channelId, joined.admin);
      });
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        const row = mut.roomOf(s.rooms, channelId);
        if (row) row.joining = false;
        s.notice = e instanceof Error ? e.message : String(e);
      });
    }
  }

  /** Answers whether it joined: a refusal has already become the notice. */
  async joinByToken(channelId: number, token: string): Promise<boolean> {
    const lifetime = currentLifetime();
    try {
      const joined = await api.joinChannel(channelId, { token });
      if (!isCurrent(lifetime)) return false;
      this.mutate((s) => {
        mut.roomsOnJoined(s.rooms, channelId, joined.admin);
      });
      return true;
    } catch (e) {
      if (!isCurrent(lifetime)) return false;
      this.fail(e);
      return false;
    }
  }

  // -------------------------------------------------------------------------
  // Link cards (docs/link-previews.md D278–D282)

  /**
   * A page's card (D280), asked of the gateway once per URL: a second want
   * while the first is queued or in the air is nothing, and so is one after
   * the answer — until a failure's wait is over (`LinkPreviewEntry`). Four at
   * a time across the app and the rest in line, first come first served.
   */
  wantLinkPreview(url: string): void {
    if (url === "" || this.previewsAsked.has(url)) return;
    if (!asksAgain(this.state.linkPreviews[url], Date.now())) return;
    const lifetime = currentLifetime();
    this.previewsAsked.add(url);
    this.mutate((s) => {
      s.linkPreviews[url] = { t: "loading" };
    });
    this.previewLane.run(async () => {
      let entry: LinkPreviewEntry;
      try {
        const preview = await api.fetchLinkPreview(url);
        entry =
          preview && typeof preview === "object"
            ? { t: "ready", preview }
            : { t: "none", retryAt: null };
      } catch (e) {
        entry = { t: "none", retryAt: askLater(e) ? Date.now() + LINK_RETRY_MS : null };
      }
      if (!isCurrent(lifetime)) return;
      this.previewsAsked.delete(url);
      this.mutate((s) => {
        s.linkPreviews[url] = entry;
      });
    });
  }

  /**
   * The card of a channel this reader holds no row of (D281) — a private one
   * somebody was sent the invite to is what it exists for. A channel they hold
   * is drawn from its row and asks nothing. Otherwise a page's discipline, in
   * a line of its own: a 404 is no card for the session.
   */
  wantChannelCard(channelId: number, token?: string | null): void {
    if (mut.roomOf(this.state.rooms, channelId)) return;
    const key = channelCardKey(channelId, token);
    if (this.cardsAsked.has(key)) return;
    if (!asksAgain(this.state.channelCards[key], Date.now())) return;
    const lifetime = currentLifetime();
    this.cardsAsked.add(key);
    this.mutate((s) => {
      s.channelCards[key] = { t: "loading" };
    });
    this.cardLane.run(async () => {
      let entry: ChannelCardEntry;
      try {
        const card = await api.fetchChannelCard(channelId, token);
        entry =
          card && typeof card === "object" ? { t: "ready", card } : { t: "none", retryAt: null };
      } catch (e) {
        entry = { t: "none", retryAt: askLater(e) ? Date.now() + LINK_RETRY_MS : null };
      }
      if (!isCurrent(lifetime)) return;
      this.cardsAsked.delete(key);
      this.mutate((s) => {
        s.channelCards[key] = entry;
      });
    });
  }

  /**
   * Join a channel from a link to it — its card's «Вступить», or an invite
   * link's own words (D279). One the reader is already in just opens.
   *
   * Without a token it is a public door, and only for a channel whose row
   * the reader holds: one they cannot see has no door a link without a token
   * could open, and no card offers one. The channel opens and the join goes
   * out.
   *
   * With a token it is the join route's own sequence (`JoinRoute`, the React
   * router's coordinator): the join, then the state — a private channel's row
   * arrives with it and not before (D115) — then the channel. A refused token
   * stops at the notice it raised, which the resync would clear.
   */
  async joinFromLink(channelId: number, token?: string | null): Promise<void> {
    const lifetime = currentLifetime();
    const row = mut.roomOf(this.state.rooms, channelId);
    if (row?.member) {
      this.openChannel(channelId);
      return;
    }
    if (!token) {
      if (!row) return;
      this.openChannel(channelId);
      await this.join(channelId);
      return;
    }
    if (this.linkJoins.has(channelId)) return;
    this.linkJoins.add(channelId);
    try {
      if (!(await this.joinByToken(channelId, token))) return;
      if (!isCurrent(lifetime)) return;
      await this.sync();
      if (!isCurrent(lifetime)) return;
      this.openChannel(channelId);
    } finally {
      if (isCurrent(lifetime)) this.linkJoins.delete(channelId);
    }
  }

  /** The conversation with a friend (D279's «Написать»): the friendship
   * carries its channel, so there is nothing to ask. A pet is nobody's
   * friend, and its dialog has a door of its own (`openPetDm`). */
  openDm(userId: number): void {
    if (this.state.account.pets !== null && isPet(this.state.directory.people[userId])) {
      void this.openPetDm(userId).catch((e: unknown) => this.fail(e));
      return;
    }
    const friend = this.state.friends.friends.find((f) => f.user_id === userId);
    if (friend) this.openChannel(friend.channel_id);
  }

  // -------------------------------------------------------------------------
  // Pets (docs/pets-plan.md D349–D368)
  //
  // The verbs a person has about a pet. Each throws its refusal to the caller,
  // which shows it where the verb was pressed (the card's notice, the
  // dialog's Restart bar); the management sheet speaks to the gateway itself,
  // the way the channel console does.

  /** The row of the dialog with `petId`, when the reader has one. */
  private petDmOf(petId: number): import("./types").Room | undefined {
    return this.state.rooms.find((r) => isDmRoom(r) && r.peerId === petId);
  }

  /**
   * The dialog with a pet (D358, the card's «Написать»): the row when there
   * is one. Otherwise the gateway starts it — the pet hears of it and may
   * answer from then on — and the row is made here from the answer, exactly as
   * its `channel_created` makes it, because that frame and this answer race
   * and the second of them is a no-op.
   */
  openPetDm(petId: number): Promise<void> {
    const held = this.petDmOf(petId);
    if (held) {
      this.openChannel(held.channelId);
      return Promise.resolve();
    }
    return this.sessionAction(
      () => api.openPetDm(petId),
      ({ channel_id }) => {
        this.mutate((s) => {
          mut.roomsOnCreated(
            s.rooms,
            channel_id,
            mut.nameOf(s.directory, petId),
            "dm",
            petId,
            petId,
            mut.selfIdOf(s.directory),
          );
        });
        this.openChannel(channel_id);
      },
    );
  }

  /** Stop (D368): nothing the reader writes reaches the pet, and it may not
   * write to them, until they send `/restart`. Every device hears it from the
   * `pet_stopped` frame; this one does not wait for it. */
  stopPet(petId: number): Promise<void> {
    return this.sessionAction(
      () => api.stopPet(petId),
      () => {
        const row = this.petDmOf(petId);
        if (!row) return;
        this.mutate((s) => {
          mut.roomsOnPetStopped(s.rooms, row.channelId, true);
        });
      },
    );
  }

  /**
   * Restart (D368): the message `/restart` into the dialog, which the gateway
   * takes as the Stop taken back and hands the pet as an ordinary command.
   * Sent straight rather than through the composer's queue — it is pressed on
   * the card as often as in the dialog, where there is no composer to queue
   * in — under the dialog's clock like anything else sent there (D298). Its
   * echo draws it; the answer is folded in so the open dialog does not wait.
   */
  restartPet(petId: number): Promise<void> {
    const row = this.petDmOf(petId);
    if (!row) return Promise.resolve();
    const channel = row.channelId;
    const ttlS = this.state.sendTtl[channel] ?? null;
    return this.sessionAction(
      () =>
        api.sendMessage(channel, {
          nonce: crypto.randomUUID(),
          text: RESTART_TEXT,
          entities: [],
          attachments: [],
          ...(ttlS !== null ? { ttl_s: ttlS } : {}),
        }),
      (answer) => {
        this.noteServerClock(answer);
        this.mutate((s) => {
          mut.roomsOnPetStopped(s.rooms, channel, false);
          mut.roomsOnMessage(s.rooms, answer, s.selectedChannel, true, Date.now(), this.t);
          if (s.feed.channelId === channel) {
            mut.reconcile(s.feed, answer, this.t);
            s.feed.lastReadSeq = Math.max(s.feed.lastReadSeq, answer.seq);
            mut.resolveNames(s.feed, s.directory, this.t);
          }
        });
        void this.markReadAt(channel, answer.seq);
      },
    );
  }

  /** A group's own admins put the pet into it (D359); the pet arrives with an
   * ordinary `membership` frame and a `joined` line that names who did it. */
  addPetToGroup(channelId: number, petId: number): Promise<void> {
    return this.sessionAction(
      () => api.addPetToGroup(channelId, petId),
      () => {
        const room = mut.roomOf(this.state.rooms, channelId);
        this.pushToast(this.t.petAdded(room?.name ?? `#${channelId}`), null);
      },
    );
  }

  /** Retire (D365) — the owner's or a server admin's, and for good. The pet's
   * own frame says so to everybody; this client does not wait for it. */
  retirePet(petId: number): Promise<void> {
    return this.sessionAction(
      () => api.retirePet(petId),
      () => {
        this.mutate((s) => {
          const pet = s.directory.people[petId];
          if (pet) pet.retired = true;
        });
        this.onPetsMoved([
          { petId, rev: this.state.directory.people[petId]?.petRev ?? 0, retired: true },
        ]);
        void this.refreshPetAbilities();
      },
    );
  }

  /** `GET /v1/me` again for `pets` alone (D363): making a pet can use up the
   * owner's allowance and retiring one can give it back, and only that answer
   * says which. Best-effort — the form's own answer was the one that mattered. */
  async refreshPetAbilities(): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const me = await api.fetchMe();
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        s.account.pets = me.pets ?? null;
      });
    } catch {
      // The button stays as it was until the next sign-in.
    }
  }

  /**
   * The chat's `/` list (D360), asked for when the composer first opens it
   * there and kept for the session until a pet's `membership` frame or its
   * card moving drops it (`dropCommands`). Nothing on a server without pets,
   * and nothing in a dialog with a person — there is no pet in it to declare
   * anything. A `loading` whose ask a teardown forgot is asked again.
   */
  wantCommands(channelId: number): void {
    const s = this.state;
    if (s.account.pets === null) return;
    const entry = s.commands[channelId];
    if (entry?.t === "ready" || (entry?.t === "loading" && this.commandAsks.has(channelId))) return;
    const room = mut.roomOf(s.rooms, channelId);
    if (!room || (isDmRoom(room) && mut.petPeerOf(room, s.directory) === null)) return;
    const ask = ++this.commandAskSeq;
    this.commandAsks.set(channelId, ask);
    const lifetime = currentLifetime();
    this.mutate((st) => {
      st.commands[channelId] = { t: "loading" };
    });
    api.fetchCommands(channelId).then(
      (commands) => {
        if (!isCurrent(lifetime) || this.commandAsks.get(channelId) !== ask) return;
        const list = Array.isArray(commands) ? commands : [];
        this.mutate((st) => {
          st.commands[channelId] = { t: "ready", commands: list };
        });
        // A pet the directory has not heard of has no name for `/name@pet`
        // and no face for its row: ask the directory once (its TTL gates it).
        if (list.some((c) => !this.state.directory.people[c.pet_id])) {
          void this.refreshDirectory(false);
        }
      },
      () => {
        if (!isCurrent(lifetime) || this.commandAsks.get(channelId) !== ask) return;
        this.mutate((st) => {
          st.commands[channelId] = { t: "none" };
        });
      },
    );
  }

  /** Forgets the `/` lists of `channels`, so the next opening asks again —
   * and asks at once for the open chat's, whose list may be on screen. An ask
   * still in the air for one of them is thrown away when it lands. */
  private dropCommands(channels: readonly number[]): void {
    const held = channels.filter((id) => this.state.commands[id] !== undefined);
    if (held.length === 0) return;
    for (const id of held) this.commandAsks.delete(id);
    this.mutate((s) => {
      for (const id of held) delete s.commands[id];
    });
    const open = this.state.feed.channelId;
    if (held.includes(open)) this.wantCommands(open);
  }

  async createChannel(name: string): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const { channel_id } = await api.createChannel(name);
      if (!isCurrent(lifetime)) return;
      await this.sync();
      if (!isCurrent(lifetime)) return;
      this.openChannel(channel_id);
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.fail(e);
    }
  }

  /**
   * A group (D274), on a channel's terms. A 404 is a gateway older than
   * groups: it has no such route and made nothing, which is said in the page's
   * language rather than as the bare status — and nothing falls back to a
   * channel, which would be public under a name meant for a few people.
   */
  async createGroup(name: string): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const { channel_id } = await api.createGroup(name);
      if (!isCurrent(lifetime)) return;
      await this.sync();
      if (!isCurrent(lifetime)) return;
      this.openChannel(channel_id);
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      if (e instanceof api.ApiError && e.status === 404) {
        this.mutate((s) => {
          s.notice = this.t.groupUnsupported();
        });
        return;
      }
      this.fail(e);
    }
  }

  // -------------------------------------------------------------------------
  // The 2 s tick: typing expiry, ring expiry, idle transitions — one clock.

  private startTick(): void {
    this.stopTick();
    this.tickTimer = setInterval(() => this.tick(), TICK_MS);
  }

  private stopTick(): void {
    if (this.tickTimer !== null) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  private noteActivity(): void {
    this.lastActivityMs = Date.now();
  }

  private tick(): void {
    const now = Date.now();
    const stopRing = this.state.ringing !== null && now - this.state.ringing.sinceMs >= RING_FOR_MS;
    this.mutate((s) => {
      mut.tickTyping(s.feed, now);
      if (stopRing) s.ringing = null;
    });
    if (stopRing) this.onRing?.(false);
    // Presence transitions (D205): in-page signals only, 300 s threshold,
    // transitions-only behind the double gate.
    const away = now - this.lastActivityMs >= AFK_AFTER_MS;
    const s = this.state;
    const speak = s.connected && s.presence.enabled && s.presence.selfAfk !== away;
    if (speak) {
      this.mutate((st) => {
        st.presence.selfAfk = away;
      });
      this.socket?.sendActivity(away);
    }
    // The expiry timer counts running time, and a machine that slept through a
    // deadline wakes with it still counting (D298): this clock looks again
    // against the wall, and stands the timer for what is actually left.
    this.expireDue();
  }

  // -------------------------------------------------------------------------

  setTab(tab: import("./types").SidebarTab): void {
    this.transition("tab", () => {
      this.mutate((s) => {
        s.tab = tab;
      });
    });
    try {
      localStorage.setItem("echofloat.tab", tab);
    } catch {
      /* Keep the live selection. */
    }
  }

  clearNotice(): void {
    this.transition("toast", () => {
      this.mutate((s) => {
        s.notice = null;
      });
    });
  }

  showNotice(text: string): void {
    this.transition("toast", () => {
      this.mutate((s) => {
        s.notice = text;
      });
    });
  }

  // ---------------------------------------------------------------------
  // The own profile (F2)

  /** `""` clears, and every answer is the whole UserJson (D64/D65). */
  saveProfile(displayName: string, bio: string): Promise<void> {
    return this.sessionAction(
      () =>
        api.updateProfile({
          username: this.state.selfUsername,
          display_name: displayName.trim(),
          bio: bio.trim(),
        }),
      (me) => this.adoptMe(me),
    );
  }

  uploadAvatar(file: File): Promise<void> {
    return this.sessionAction(
      async (check) => {
        const result = await upload(file, "photo", { fileName: file.name }, () => {}, {});
        check();
        return api.setAvatar(result.file_id);
      },
      (me) => this.adoptMe(me),
    );
  }

  removeAvatar(): Promise<void> {
    return this.sessionAction(
      () => api.deleteAvatar(),
      (me) => this.adoptMe(me),
    );
  }

  /** R44: the change revokes older tokens, so the socket's next rejection is
   * expected — the window is armed BEFORE the request, the fresh JWT is
   * adopted from the answer, and the socket redials with it instead of
   * logging out. */
  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    armReauthWindow();
    return this.sessionAction(
      () => api.changePassword(currentPassword, newPassword),
      ({ jwt }) => {
        const s = session();
        if (s) setSession({ ...s, jwt });
        this.startSocket(); // redial with the fresh JWT
      },
    );
  }

  // ---------------------------------------------------------------------
  // The user card + friends (F3)

  /** Always a FRESH friendship read (F3): the card shows a placeholder
   * while it flies, never defaults. */
  openCard(userId: number): void {
    const generation = ++this.cardGeneration;
    // The recheck stays INSIDE the swap callback: a view transition runs its
    // callback at capture time (next frame), and recheckCard reads
    // state.card.userId — called outside, it would see the card still closed.
    this.transition("sheet", () => {
      if (generation !== this.cardGeneration) return;
      this.mutate((s) => {
        s.card = { userId, relation: null, pet: null };
      });
      void this.recheckCard();
    });
  }

  closeCard(): void {
    const generation = ++this.cardGeneration;
    this.transition("sheet", () => {
      if (generation !== this.cardGeneration) return;
      this.mutate((s) => {
        s.card = { userId: null, relation: null, pet: null };
      });
    });
  }

  /** Re-read after an action moved the relation. */
  reopenCard(): Promise<void> {
    return this.recheckCard();
  }

  addFriend(userId: number): Promise<void> {
    return this.sessionAction(() => api.sendFriendRequest(userId));
  }

  cancelRequest(userId: number): Promise<void> {
    return this.sessionAction(() => api.cancelFriendRequest(userId));
  }

  acceptRequest(userId: number): Promise<void> {
    return this.sessionAction(() => api.acceptFriendRequest(userId));
  }

  /** Silent by design (D171): nothing reaches the sender. */
  declineRequest(userId: number): Promise<void> {
    return this.sessionAction(() => api.declineFriendRequest(userId));
  }

  unfriendUser(userId: number): Promise<void> {
    return this.sessionAction(() => api.unfriend(userId));
  }

  blockUser(userId: number): Promise<void> {
    return this.sessionAction(() => api.blockUser(userId));
  }

  unblock(userId: number): Promise<void> {
    return this.sessionAction(() => api.unblockUser(userId));
  }

  /** `auto | dnd | invisible` (D183); the answer is the whole UserJson. */
  setManualStatus(status: "auto" | "dnd" | "invisible"): Promise<void> {
    return this.sessionAction(
      () => api.setStatus(status),
      (me) => this.adoptMe(me),
    );
  }

  /** Decline a DM call (D178): per-callee and silent-to-others. */
  async declineIncomingCall(): Promise<void> {
    const lifetime = currentLifetime();
    const ring = this.state.ringing;
    if (!ring) return;
    this.transition("toast", () => {
      this.mutate((s) => {
        s.ringing = null;
      });
    });
    this.onRing?.(false);
    const row = this.state.rooms.find((r) => r.channelId === ring.channelId);
    if (row && row.kind === "dm") {
      await api.declineCall(ring.channelId).catch(() => {});
      if (!isCurrent(lifetime)) return;
    }
  }

  // -------------------------------------------------------------------------
  // Voice (F4)

  private voice: VoiceSession | null = null;

  async joinVoice(channelId: number): Promise<void> {
    const lifetime = currentLifetime();
    if (!voiceSupported()) {
      this.showNotice(this.t.voiceUnsupported());
      return;
    }
    if (this.state.voice?.channelId === channelId) return;
    if (this.voice) this.leaveVoice();
    const sess = session();
    if (!sess) return;
    // Answering by joining IS the answer — the ring card goes away.
    if (this.state.ringing?.channelId === channelId) {
      this.transition("toast", () => {
        this.mutate((s) => {
          s.ringing = null;
        });
      });
      this.onRing?.(false);
    }
    this.mutate((s) => {
      s.voice = mut.voiceConnecting(channelId);
    });
    const voice = new VoiceSession({
      jwt: sess.jwt,
      events: this.voiceEvents(channelId, () => this.voice === voice),
    });
    this.voice = voice;
    try {
      await voice.join(channelId, false);
      if (!isCurrent(lifetime)) return;
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      if (this.voice === voice) {
        this.voice = null;
        this.mutate((s) => {
          s.voice = null;
        });
        const text = e instanceof Error ? e.message : String(e);
        this.showNotice(
          /getUserMedia|Permission|NotAllowed/i.test(text) ? this.t.voiceMicDenied() : text,
        );
        voice.leave();
      }
    }
  }

  leaveVoice(): void {
    if (this.voice) sounds.play("leave");
    this.voice?.leave();
    this.voice = null;
    this.mutate((s) => {
      s.voice = null;
    });
  }

  toggleMute(): void {
    const v = this.state.voice;
    if (!v || !this.voice) return;
    const next = !v.muted;
    sounds.play(next ? "mute" : "unmute");
    this.voice.setMuted(next);
    this.mutate((s) => {
      if (s.voice) s.voice.muted = next;
    });
  }

  toggleDeafen(): void {
    const v = this.state.voice;
    if (!v || !this.voice) return;
    const next = !v.deafened;
    this.voice.setDeafened(next);
    this.mutate((s) => {
      if (s.voice) s.voice.deafened = next;
    });
  }

  setVoiceVolume(userId: number, volume: number): void {
    this.voice?.setVolume(userId, volume);
    this.mutate((s) => {
      if (s.voice) s.voice.volumes[userId] = volume;
    });
  }

  // -------------------------------------------------------------------------
  // Stream viewing (F5)

  watchStream(userId: number): void {
    const v = this.state.voice;
    const peer = v?.peers.find((p) => p.userId === userId);
    if (!v || !peer || !peer.screenSharing || !this.voice) return;
    if (!shareViewSupported()) {
      this.showNotice(this.t.voiceUnsupported());
      return;
    }
    this.voice.watch({
      senderId: peer.senderId,
      hasAudio: peer.screenShareHasAudio,
    });
    this.voice.setShareVolume(1);
    this.mutate((s) => {
      if (s.voice) {
        s.voice.share = {
          userId,
          paused: peer.screenSharePaused,
          hasAudio: peer.screenShareHasAudio,
          volume: 1,
        };
      }
    });
  }

  stopWatching(): void {
    this.voice?.unwatch();
    this.mutate((s) => {
      if (s.voice) s.voice.share = null;
    });
  }

  setShareVolume(volume: number): void {
    this.voice?.setShareVolume(volume);
    this.mutate((s) => {
      if (s.voice?.share) s.voice.share.volume = volume;
    });
  }

  /** The stream view's canvas registers its painter for decoded frames;
   * null unregisters. Frames are VideoFrames the painter must close. */
  private sharePainterOwner: object | null = null;

  registerSharePainter(
    painter: ((senderId: number, frame: VideoFrame) => void) | null,
  ): () => void {
    const owner = {};
    const voice = this.voice;
    this.sharePainterOwner = owner;
    voice?.setPainter(painter);
    return () => {
      if (this.sharePainterOwner !== owner) return;
      this.sharePainterOwner = null;
      if (this.voice === voice) voice?.setPainter(null);
    };
  }

  private shareEndText(reason: number): string {
    switch (reason) {
      case 2:
        return this.t.streamEndedSourceGone();
      case 3:
        return this.t.streamEndedLeft();
      case 4:
        return this.t.streamEndedAdmin();
      default:
        return this.t.streamEndedStopped();
    }
  }

  /** Every VoiceSession event lands in the store through here; the guard
   * keeps a late event from a torn-down session off the next call's state. */
  private voiceEvents(channelId: number, ownsCall: () => boolean): VoiceEvents {
    const lifetime = currentLifetime();
    const mutateVoice = (fn: (v: import("./types").VoiceCall) => void) => {
      this.mutate((s) => {
        if (s.voice && s.voice.channelId === channelId) fn(s.voice);
      });
    };
    const events: VoiceEvents = {
      onUp: () => {
        sounds.play("join");
        mutateVoice((v) => {
          v.phase = "up";
        });
      },
      onRoster: (peers) =>
        mutateVoice((v) => {
          v.peers = peers.map((p) => ({ ...p }));
        }),
      onPeerJoined: (peer) => {
        sounds.play("join");
        mutateVoice((v) => mut.voicePeerUpsert(v, { ...peer }));
      },
      onPeerLeft: (userId) => {
        sounds.play("leave");
        mutateVoice((v) => mut.voicePeerLeft(v, userId));
      },
      onPeerVoice: (userId, muted, deafened) =>
        mutateVoice((v) => mut.voicePeerFlags(v, userId, muted, deafened)),
      onPeerRtt: (userId, rttMs) => mutateVoice((v) => mut.voicePeerRtt(v, userId, rttMs)),
      onServerMute: (userId, muted) => mutateVoice((v) => mut.voiceServerMute(v, userId, muted)),
      onSpeakers: (userIds) =>
        mutateVoice((v) => {
          v.speaking = userIds;
        }),
      onAudience: (audience) =>
        mutateVoice((v) => {
          v.audience = audience;
        }),
      onPeerShare: (userId, share) => {
        const wasWatching = this.state.voice?.share?.userId === userId;
        // On the edge only (D342): the roster a joiner is handed comes through
        // `onRoster`, not here, so a share already running says nothing — but
        // a pause is reported as the same active share again, and is no edge.
        const wasSharing =
          this.state.voice?.peers.find((p) => p.userId === userId)?.screenSharing === true;
        if (share.active !== wasSharing) {
          sounds.play(share.active ? "stream_start" : "stream_end");
        }
        mutateVoice((v) =>
          mut.voicePeerShare(v, userId, share.active, share.paused, share.hasAudio),
        );
        if (wasWatching && !share.active) {
          this.voice?.unwatch();
          this.showNotice(this.shareEndText(share.reason));
        }
      },
      onCode: (code) =>
        mutateVoice((v) => {
          v.code = code;
        }),
      onStats: (stats) =>
        mutateVoice((v) => {
          v.rttMs = stats.rttMs;
          v.lossPct = stats.lossPct;
          v.concealmentPct = stats.concealmentPct;
        }),
      onEnded: (reason) => {
        if (this.state.voice?.channelId !== channelId) return;
        this.voice = null;
        this.mutate((s) => {
          s.voice = null;
        });
        if (reason === "dropped" || reason === "serverError") {
          this.showNotice(this.t.voiceDropped());
        }
      },
    };
    return Object.fromEntries(
      Object.entries(events).map(([name, callback]) => [
        name,
        (...args: unknown[]) => {
          if (isCurrent(lifetime) && ownsCall()) Reflect.apply(callback, events, args);
        },
      ]),
    ) as unknown as VoiceEvents;
  }
}

function splicePage(
  feed: import("./types").FeedState,
  page: import("../gen/dto").PageResponse,
  atEnd: boolean,
  t: Dict,
): void {
  const first = page.messages[0]?.seq;
  const last = page.messages.at(-1)?.seq;
  const messages = page.messages.map(sanitizeMessage);
  for (const message of messages) {
    // Refreshes also restore edits missed during disconnection, and a history
    // page may itself be the first acknowledgement of a pending send.
    if (
      feed.bubbles.some(
        (b) =>
          b.message.seq === message.seq ||
          (message.nonce !== "" && b.message.nonce === message.nonce && b.state !== "sent"),
      )
    ) {
      mut.reconcile(feed, message, t);
    }
  }
  mut.appendMessages(feed, messages, atEnd);
  // The span half matters even when append inserts nothing — a visible-range
  // re-fetch after a socket drop is all duplicates, and the chips are the
  // part that still has to land (D160).
  if (first !== undefined && last !== undefined) {
    mut.applyPageReactions(feed, [first, last], page.reactions ?? [], page.my_reactions ?? []);
  }
}

/** The lossy-page rule (chat-api.md §7) at the JSON boundary: an element the
 * generated types cannot vouch for still has to be placeable; one that is
 * not even placeable is dropped, not guessed at. Runtime shape-checking is
 * deliberately shallow — the visible-line rule is enforced by rendering
 * unknown service kinds, not by rejecting rows. */
function sanitizeMessage(m: Message): Message {
  return m;
}

function stripTag(m: Message & { t?: string }): Message {
  if (!("t" in m)) return m;
  const { t: _tag, ...rest } = m;
  return rest as unknown as Message;
}

function normalizeManual(v: unknown): "dnd" | "invisible" | null {
  return v === "dnd" || v === "invisible" ? v : null;
}

/** What an optimistic bubble says of its lifetime (D298): the lifetime, and no
 * deadline — nothing is counting until somebody else has read it (D302), which
 * cannot have happened to a message that has not been sent yet. Absent, like
 * the flag beside it, on a message that stays. */
function optimisticTtl(ttlS: number | null): { ttl_s?: number } {
  return ttlS !== null ? { ttl_s: ttlS } : {};
}

/** Whether a link card's held answer is worth asking for again: none held; a
 * `loading` whose ask is gone — the ask itself is tracked apart, so this is
 * one a torn-down runtime left behind; or a failure whose wait is over. */
function asksAgain(entry: LinkPreviewEntry | ChannelCardEntry | undefined, now: number): boolean {
  if (entry === undefined || entry.t === "loading") return true;
  return entry.t === "none" && entry.retryAt !== null && now >= entry.retryAt;
}

/** Whether a link card that failed may be asked for again later (D280): over
 * the budget (429), a fetch that outlived its request (503), whatever the
 * network or the server's own trouble did (0, 5xx). A 404 — nothing to show,
 * a refused address, a gateway older than the route — and every other refusal
 * (a 400 for an address that is not http(s)) is the answer for the session. */
function askLater(e: unknown): boolean {
  const status = e instanceof api.ApiError ? e.status : 0;
  return status === 0 || status === 429 || status >= 500;
}

/** A few requests in the air and the rest in line, first come first served.
 * `clear` forgets the line and the count together — an account that ended
 * takes its requests with it — and a request of the old generation that
 * settles afterwards touches neither. */
class Lane {
  private readonly line: Array<() => Promise<void>> = [];
  private flying = 0;
  private generation = 0;

  constructor(private readonly width: number) {}

  run(job: () => Promise<void>): void {
    this.line.push(job);
    this.pump();
  }

  clear(): void {
    this.line.length = 0;
    this.flying = 0;
    this.generation += 1;
  }

  private pump(): void {
    while (this.flying < this.width) {
      const job = this.line.shift();
      if (!job) return;
      this.flying += 1;
      const generation = this.generation;
      void job()
        .catch(() => {})
        .finally(() => {
          if (generation !== this.generation) return;
          this.flying -= 1;
          this.pump();
        });
    }
  }
}

export interface StagedFile {
  file: File;
  /** What the file *is*. Not necessarily what it is sent as — see `asFile`. */
  kind: import("../gen/dto").FileKind;
  /**
   * «Send as file» (D233): upload the original bytes untouched, as a file row
   * with no thumbnail and no preview, instead of the two light weights a
   * picture is normally reduced to. A flag beside `kind` rather than an
   * overwrite of it, because the ceiling that applies is still the picture's.
   */
  asFile?: boolean;
  previewUrl?: string;
  /** Voice notes carry their packed waveform, already base64. */
  waveform?: string;
  /** Voice notes: the recorder measured it; the server has no decoder. */
  durationMs?: number;
  spoiler?: boolean;
}

/** What this attachment travels as, which is its kind unless its sender chose
 * to send a picture as a file (D233). */
function sendKindOf(item: StagedFile): import("../gen/dto").FileKind {
  return item.asFile ? "file" : item.kind;
}

/** The share of a picture's upload progress the thumbnail gets: it is a
 * twentieth of the bytes, and a bar that sat at 50 % for the thumbnail would
 * be lying about which half is the wait. */
const THUMB_SHARE = 0.15;

/** width/height are read BEFORE uploading — the server has no decoder for
 * video and trusts the client (chat-api.md §1.3).
 *
 * Photos do not come here any more: their geometry is whatever the weights
 * came out as (`lib/photo.ts`), and an original sent as a *file* has no
 * geometry to declare — a file row draws no picture. */
async function probeFile(
  item: StagedFile,
  lifetime: SessionLifetime,
): Promise<{ width?: number; height?: number; durationMs?: number }> {
  assertCurrent(lifetime);
  if (item.kind === "video" || item.kind === "video_message") {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(item.file);
      const v = document.createElement("video");
      const cleanup = () => {
        lifetime.signal.removeEventListener("abort", cancel);
        v.onloadedmetadata = null;
        v.onerror = null;
        v.removeAttribute("src");
        v.load();
        URL.revokeObjectURL(url);
      };
      const cancel = () => {
        cleanup();
        reject(lifetime.signal.reason);
      };
      lifetime.signal.addEventListener("abort", cancel, { once: true });
      v.preload = "metadata";
      v.onloadedmetadata = () => {
        const out = {
          width: v.videoWidth,
          height: v.videoHeight,
          durationMs: Math.round(v.duration * 1000),
        };
        cleanup();
        resolve(out);
      };
      v.onerror = () => {
        cleanup();
        resolve({});
      };
      v.src = url;
    });
  }
  if (item.kind === "voice" && item.durationMs !== undefined) {
    return { durationMs: item.durationMs };
  }
  return {};
}
